import { VaultDocumentReviewRecordSchema } from './vault-document.dto';
import { z } from "zod/v3";
import {
  ControlEvidenceStatusEnumSchema,
  type ControlEvidenceStatusEnum,
  EvidenceReviewStatusSchema,
  TemplateTypeSchema,
  AppTypeSchema,
  PublisherTypeSchema,
  LicenseTypeSchema,
  VerificationBadgeSchema,
  SubtaskSourceSchema,
  PrioritySchema,
} from "../generated/enums";
import { TemplateSamplingConfigSchema } from "./population.dto";
import { LabelDTOSchema } from "./label.dto";
import { LinkedPolicyClaimDTOSchema } from "./policy.dto";
import { SlaAggregateDTOSchema, SlaEvaluatedItemDTOSchema } from "./sla.dto";
import {
  EvidenceSlotCitationDTOSchema,
  type EvidenceSlotCitationDTO,
} from "./evidence-slot.dto";

// ===== Validation Type Names (predefined options for users) =====
export const ValidationTypeNameSchema = z.enum([
  // Screenshot validation types
  "access_denied",
  "successful_access",
  "user_role_visible",
  "audit_trail",
  "configuration_screen",
  "error_message",
  "approval_workflow",
  "user_management",
  "data_classification",
  "encryption_indicator",
  "backup_confirmation",
  "mfa_setup",
  // Text validation types
  "clear_conclusion",
  "exception_documented",
  "timestamp_present",
  "tester_identified",
  // URL validation types
  "production_url",
  "secure_url",
  // Generic
  "present",
  "custom",
]);

export type ValidationTypeName = z.infer<typeof ValidationTypeNameSchema>;

// ===== RFC-103: Pagination Configuration Schemas =====

/** URL-based pagination configuration */
export const PaginationUrlConfigSchema = z.object({
  /** URL pattern with {page} placeholder, e.g., "https://app.com/users?page={page}" */
  pattern: z.string(),
  /** Starting page number (usually 1, sometimes 0) */
  startPage: z.number().default(1),
});

export type PaginationUrlConfig = z.infer<typeof PaginationUrlConfigSchema>;

/** Selector-based pagination configuration */
export const PaginationSelectorConfigSchema = z.object({
  /** CSS selector for the "next" button */
  nextSelector: z.string(),
  /** Time to wait after clicking next (ms), default 1500 */
  waitAfterClick: z.number().default(1500),
});

export type PaginationSelectorConfig = z.infer<
  typeof PaginationSelectorConfigSchema
>;

/** Sample strategy configuration */
export const PaginationSampleConfigSchema = z.object({
  /** Hard limit on total pages to capture, default 10 */
  maxPages: z.number().default(10),
  /** Number of first pages to capture, default 2 */
  captureFirst: z.number().default(2),
  /** Number of last pages to capture, default 2 */
  captureLast: z.number().default(2),
  /** Number of evenly-spaced middle pages to sample, default 3 */
  sampleMiddle: z.number().default(3),
});

export type PaginationSampleConfig = z.infer<
  typeof PaginationSampleConfigSchema
>;

/** First/Last strategy configuration */
export const PaginationFirstLastConfigSchema = z.object({
  /** Number of first pages to capture, default 1 */
  captureFirst: z.number().default(1),
  /** Number of last pages to capture, default 1 */
  captureLast: z.number().default(1),
  /** Safety cap on max pages, default 10 */
  maxPages: z.number().default(10),
});

export type PaginationFirstLastConfig = z.infer<
  typeof PaginationFirstLastConfigSchema
>;

/** All pages strategy configuration */
export const PaginationAllConfigSchema = z.object({
  /** Safety cap on max pages, default 20 */
  maxPages: z.number().default(20),
});

export type PaginationAllConfig = z.infer<typeof PaginationAllConfigSchema>;

/** Step pagination configuration (RFC-103) */
export const StepPaginationConfigSchema = z.object({
  enabled: z.boolean(),
  /** Navigation method: 'url' for direct URL navigation, 'selector' for clicking next button */
  method: z.enum(["url", "selector"]),
  /** URL-based pagination configuration (preferred when available) */
  urlConfig: PaginationUrlConfigSchema.optional(),
  /** Selector-based pagination configuration (fallback) */
  selectorConfig: PaginationSelectorConfigSchema.optional(),
  /** Capture strategy */
  strategy: z.enum(["sample", "firstLast", "all"]),
  /** Strategy-specific configuration for 'sample' strategy */
  sampleConfig: PaginationSampleConfigSchema.optional(),
  /** Strategy-specific configuration for 'firstLast' strategy */
  firstLastConfig: PaginationFirstLastConfigSchema.optional(),
  /** Strategy-specific configuration for 'all' strategy */
  allConfig: PaginationAllConfigSchema.optional(),
});

export type StepPaginationConfig = z.infer<typeof StepPaginationConfigSchema>;

// ===== Template Workflow Step (for executionConfig) =====
export const TemplateWorkflowStepSchema = z.object({
  id: z.string(),
  /** Step action type */
  action: z.enum([
    "navigate",
    "click",
    "type",
    "extract",
    "wait",
    "scroll",
    "hover",
    "select",
    "upload",
    "download",
    "screenshot",
  ]),
  /** URL for navigation steps */
  url: z.string().optional(),
  /** Human-readable description of the step */
  description: z.string().optional(),
  /** Whether to capture a screenshot after this step */
  screenshot: z.boolean().optional(),
  /** Selector for interactive steps */
  selector: z.string().optional(),
  /** Value for type/select actions */
  value: z.string().optional(),
  /** RFC-103: Pagination configuration for this step */
  pagination: StepPaginationConfigSchema.optional(),
});

export type TemplateWorkflowStep = z.infer<typeof TemplateWorkflowStepSchema>;

// ===== Template Execution Config (stored in GuidedTemplate.executionConfig) =====
// Import workflow schemas and types for proper typing and validation
import type {
  WorkflowStepDTO,
  WorkflowParamDTO,
  WorkflowOutputDTO,
} from "./workflow.dto";
import {
  WorkflowStepSchema,
  WorkflowParamSchema,
  WorkflowOutputSchema,
} from "./workflow.dto";

const FindingGuidanceRemediationStepSchema = z.object({
  channel: z.enum([
    "console",
    "cli",
    "terraform",
    "native_iac",
    "manual",
    "other",
  ]),
  title: z.string(),
  body: z.string(),
  referenceUrl: z.string().optional(),
});

const FindingGuidanceSchema = z.object({
  source: z.object({
    name: z.string(),
    url: z.string().optional(),
  }),
  title: z.string().optional(),
  summary: z.string().optional(),
  risk: z.string().optional(),
  references: z.array(z.string()).optional(),
  relatedUrls: z.array(z.string()).optional(),
  requiredServices: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  resourceType: z.string().optional(),
  auditProcedure: z.string().optional(),
  remediationProcedure: z.string().optional(),
  notes: z.string().optional(),
  dependsOn: z.array(z.string()).optional(),
  relatedChecks: z.array(z.string()).optional(),
  fixerAvailable: z.boolean().optional(),
  remediationSteps: z.array(FindingGuidanceRemediationStepSchema).optional(),
});

export const TemplateExecutionConfigSchema = z.object({
  /** Target URL for static screenshot tests */
  targetUrl: z.string().optional(),
  /** RFC-103: Pagination configuration for static screenshot tests */
  pagination: StepPaginationConfigSchema.optional(),
  /** Workflow steps to execute (WorkflowStepDTO[]) */
  steps: z.array(WorkflowStepSchema).optional(),
  /** Workflow params (Record<string, WorkflowParamDTO>) */
  params: z.record(WorkflowParamSchema).optional(),
  /** Workflow-level success criteria */
  successCriteria: z.string().optional(),
  /** Expected outputs from workflow execution */
  outputs: z.array(WorkflowOutputSchema).optional(),
  /** Whether execution is automated */
  automated: z.boolean().optional(),
  /** Browser profile requirements */
  browserProfile: z
    .object({
      required: z.boolean().optional(),
      loginUrl: z.string().optional(),
    })
    .optional(),
  // RFC-104: POLICY type fields
  /** Policy type identifier (for POLICY templates) */
  policyType: z.string().optional(),
  /** Whether policy requires acknowledgment (for POLICY templates) */
  requiresAcknowledgment: z.boolean().optional(),
  // RFC-139: Connector / scan metadata fields
  /** Origin of the evidence record (e.g. 'prowler', 'manual', 'integration') */
  sourceType: z.string().optional(),
  /** Idempotency key to deduplicate evidence submissions */
  idempotencyKey: z.string().optional(),
  /** ID of the integration connection that produced this evidence */
  connectionId: z.string().optional(),
  /** Logical control reference in the originating tool (e.g. Prowler check group) */
  controlRef: z.string().optional(),
  /** Source system identifier (e.g. 'aws', 'gcp', 'azure') */
  system: z.string().optional(),
  /** Template key used for matching / deduplication across runs */
  templateKey: z.string().optional(),
  /** Specific tool that generated the finding (e.g. 'prowler', 'trivy') */
  tool: z.string().optional(),
  /** Provider-specific check / rule identifier */
  checkId: z.string().optional(),
  /** Cloud / SaaS provider (e.g. 'aws', 'github', 'okta') */
  provider: z.string().optional(),
  /** Cloud service or product (e.g. 'iam', 's3', 'ec2') */
  service: z.string().optional(),
  /** Finding severity level */
  severity: z.enum(["critical", "high", "medium", "low"]).optional(),
  /** Normalized remediation and risk metadata from external knowledge sources */
  guidance: FindingGuidanceSchema.optional(),
});

// Provide strongly-typed interface for better type safety
export interface TemplateExecutionConfig {
  targetUrl?: string;
  pagination?: StepPaginationConfig;
  steps?: WorkflowStepDTO[];
  params?: Record<string, WorkflowParamDTO>;
  successCriteria?: string;
  outputs?: WorkflowOutputDTO[];
  automated?: boolean;
  browserProfile?: {
    required?: boolean;
    loginUrl?: string;
  };
  policyType?: string;
  requiresAcknowledgment?: boolean;
  // RFC-139: Connector / scan metadata fields
  sourceType?: string;
  idempotencyKey?: string;
  connectionId?: string;
  controlRef?: string;
  system?: string;
  templateKey?: string;
  tool?: string;
  checkId?: string;
  provider?: string;
  service?: string;
  severity?: "critical" | "high" | "medium" | "low";
  guidance?: z.infer<typeof FindingGuidanceSchema>;
}

// Import PopulationType and SamplingMethod from generated enums (RFC-104)
import { PopulationTypeSchema, SamplingMethodSchema } from "../generated/enums";
export type { PopulationType, SamplingMethod } from "../generated/enums";

// ===== Simplified Element (what users configure) =====
// type defines WHAT to validate in the evidence, not HOW evidence is collected.
// Collection method is determined by templateType for template-backed evidence.
// Runbooks/manual guided procedures are Workflow-backed after RFC-167.
export const SimplifiedElementSchema = z.object({
  id: z.string(),
  // What type of content to validate in the evidence
  // - screenshot: Validate image content (UI screenshots, configuration screens)
  // - text: Validate text content (conclusions, explanations, policy excerpts)
  // - url: Validate URL presence (links, references)
  // - metadata: System-captured data (auto-validated)
  // - document_template: Operational document that must exist (NDA, vendor questionnaire, etc.)
  //   Can be AI-generated or uploaded by the user.
  // Note: File upload is implicit for POLICY templates - elements validate document CONTENT
  type: z.enum(["screenshot", "text", "url", "metadata", "document_template"]),
  name: z.string(),
  description: z.string(),
  validationType: ValidationTypeNameSchema,
  customPrompt: z.string().optional(),
  weight: z.number().min(0).max(100),
  required: z.boolean(),
  minCount: z.number().optional(),
  // document_template metadata — only relevant when type === "document_template"
  templateSlug: z.string().optional(),
  templateTitle: z.string().optional(),
  generatable: z.boolean().optional(),
});

export type SimplifiedElement = z.infer<typeof SimplifiedElementSchema>;

/**
 * Issue #972 — auditor-grade evidence guidance ("what your auditor will accept").
 * Curated once per canonical slot in the committed catalog
 * (config/compliance/evidence-guidance-catalog.json), snapshotted onto each CES
 * at materialization, and editable per-workspace. The five content dimensions map
 * to the auditor-expectation breakdown in the issue.
 */
export const AuditorEvidenceGuidanceSchema = z.object({
  /** Concrete, named artifacts an auditor will accept as evidence. */
  acceptableArtifacts: z.array(z.string()),
  /** Expected format / medium (e.g. system-generated export vs screenshot). */
  format: z.string(),
  /** Separation-of-duties expectations (who prepares vs approves). */
  separationOfDuties: z.string(),
  /** Frequency / recency the auditor expects (e.g. within the audit period). */
  frequency: z.string(),
  /** Population-vs-sample guidance (full enumeration vs sampled rows). */
  populationVsSample: z.string(),
  /** One- to two-sentence summary of what the auditor is really checking for. */
  auditorPerspective: z.string(),
  /** Provenance — ISO timestamp of generation. */
  generatedAt: z.string().optional(),
  /** SCREENATA_CONTROLS_VERSION the guidance was generated against. */
  catalogVersion: z.number().optional(),
});

export type AuditorEvidenceGuidance = z.infer<
  typeof AuditorEvidenceGuidanceSchema
>;

/**
 * Read `acceptableArtifacts` off a persisted `AuditorEvidenceGuidance` blob.
 *
 * The column is Prisma JSON handed to clients through an unchecked cast, so the
 * shape is asserted, not guaranteed — every consumer has to guard, and three of
 * them had grown their own copy of this check. Deliberately field-level rather
 * than `AuditorEvidenceGuidanceSchema.safeParse`: all six content fields are
 * required, so a whole-object parse would discard a partially-populated
 * snapshot that renders fine today.
 *
 * The entries are ALTERNATIVES — any one satisfies the test. Consumers that
 * show them to a human or a model must say so; a bare list reads as a checklist.
 */
export function readAcceptableArtifacts(guidance: unknown): string[] {
  if (!guidance || typeof guidance !== "object" || Array.isArray(guidance)) {
    return [];
  }
  const artifacts = (guidance as Record<string, unknown>).acceptableArtifacts;
  if (!Array.isArray(artifacts)) return [];
  return artifacts.filter(
    (artifact): artifact is string =>
      typeof artifact === "string" && artifact.trim().length > 0,
  );
}

/**
 * `type: "text"` elements are typed in AFTER recording — they never gated a
 * capture (the progress counter has always ignored them) but they did drive the
 * post-recording "Missing Requirements" nag, became weighted scoring criteria,
 * and were fed to the copilot LLM as permanently-pending work. Claim-derived
 * tests get one per policy commitment, which made a three-line checklist
 * mandatory on tests whose evidence is a single export.
 *
 * They are now advisory everywhere. Deliberately NOT exported: every consumer
 * must go through {@link resolveEvidenceChecklist} so there is exactly one
 * place that decides what a collector or a grader sees. Flip this predicate to
 * bring them back.
 */
function isAdvisoryElement(element: { type?: string } | null | undefined): boolean {
  return element?.type === "text";
}

function asElementArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export interface EvidenceChecklistSources {
  /** `ControlEvidenceStatus.requiredElements` — the per-workspace snapshot. */
  cesElements?: unknown;
  /** `GuidedTemplate.scoringCriteria.requiredElements` — the fallback. */
  templateElements?: unknown;
}

/**
 * THE single way to obtain the elements a collector must capture or a grader
 * must score. Resolves CES-vs-template precedence and drops advisory elements.
 *
 * Both halves used to be reimplemented per call site — the precedence in at
 * least four places (two of them inside one file), the filtering in none — so a
 * fix applied to one reader silently missed the rest. `TaskDTO` and
 * `TemplateForRecordingDTO` therefore expose a resolved `evidenceChecklist`
 * rather than the raw list, which makes a missed consumer a compile error
 * instead of a bug you find by watching the AI misbehave.
 *
 * Precedence: a CES that defines its OWN elements wins, even when every one of
 * them is advisory and the result is empty. Falling through to the template in
 * that case would resurrect a stale checklist the test deliberately replaced.
 *
 * Authoring surfaces (template editor, SOP import, subtask builder) keep raw
 * access via `ScoringCriteria` — they edit the list rather than consume it.
 */
export function resolveEvidenceChecklist(
  sources: EvidenceChecklistSources,
): SimplifiedElement[] {
  const cesElements = asElementArray<SimplifiedElement>(sources.cesElements);
  const chosen =
    cesElements.length > 0
      ? cesElements
      : asElementArray<SimplifiedElement>(sources.templateElements);
  return chosen.filter((element) => !isAdvisoryElement(element));
}

/**
 * The judging dimensions of the #972 guidance, in the order an auditor reasons
 * about them. `acceptableArtifacts` is deliberately absent — it is rendered as
 * the artifact list, not as a judging criterion.
 *
 * Shared so the web test-detail page and the extension sidepanel show the same
 * dimensions, in the same order, under the same labels — and so both stay in
 * step with the grader, which is fed these exact fields
 * (`evidence-scoring.adhoc.ts`). A per-surface copy drifts silently.
 */
export const AUDITOR_JUDGING_DIMENSIONS = [
  { key: "auditorPerspective", label: "What they check" },
  { key: "format", label: "Format" },
  { key: "frequency", label: "Recency" },
  { key: "populationVsSample", label: "Population vs sample" },
  { key: "separationOfDuties", label: "Who prepares vs approves" },
] as const satisfies ReadonlyArray<{
  key: keyof AuditorEvidenceGuidance;
  label: string;
}>;

/**
 * Read the populated judging dimensions off a persisted guidance blob, in
 * {@link AUDITOR_JUDGING_DIMENSIONS} order. Field-level guarding for the same
 * reason as {@link readAcceptableArtifacts} — a partially-populated snapshot
 * must still render whatever it does carry.
 */
export function readAuditorJudgingDimensions(
  guidance: unknown,
): Array<{ label: string; value: string }> {
  if (!guidance || typeof guidance !== "object" || Array.isArray(guidance)) {
    return [];
  }
  const record = guidance as Record<string, unknown>;
  return AUDITOR_JUDGING_DIMENSIONS.flatMap(({ key, label }) => {
    const value = record[key];
    if (typeof value !== "string") return [];
    const trimmed = value.trim();
    return trimmed ? [{ label, value: trimmed }] : [];
  });
}

// ===== Quality Thresholds =====
export const QualityThresholdsSchema = z.object({
  minimum: z.number().min(0).max(100),
  acceptable: z.number().min(0).max(100),
  excellent: z.number().min(0).max(100),
});

export type QualityThresholds = z.infer<typeof QualityThresholdsSchema>;

// ===== LLM Generation Schemas (with descriptions for prompts) =====
// These schemas are used by AI agents (sop-parser, control-matrix-converter) to generate templates

/**
 * Simplified sampling config for LLM generation
 * Has descriptions that help the LLM understand each field
 */
export const LLMSamplingConfigSchema = z.object({
  requiresPopulation: z
    .boolean()
    .describe(
      'True if the control requires sampling from a population (e.g., "review a sample of terminated users")',
    ),
  populationType: PopulationTypeSchema.optional().describe(
    "Type of population to sample from",
  ),
  populationFields: z
    .array(z.string())
    .optional()
    .describe(
      "Fields to collect for each population item, only if populationType is CUSTOM",
    ),
  minimumSampleSizeRecommended: z
    .number()
    .optional()
    .describe("Recommended minimum sample size (e.g., 5, 10, 25)"),
  recommendedMethod: SamplingMethodSchema.optional().describe(
    "How to select items from the population",
  ),
  sharedEvidenceSupported: z
    .boolean()
    .optional()
    .describe(
      "True = one screenshot can show all samples (e.g., filtered list view). False = separate evidence per sample item.",
    ),
});

export type LLMSamplingConfig = z.infer<typeof LLMSamplingConfigSchema>;

/**
 * Subtask definition for LLM generation
 * Instructions are required since testers rely on them for guidance
 */
export const LLMSubtaskDefinitionSchema = z.object({
  id: z
    .string()
    .describe('Unique identifier (kebab-case, e.g., "vendor-inventory")'),
  name: z.string().describe("Display name for the subtask"),
  description: z
    .string()
    .describe(
      "Brief summary (1 sentence) of WHAT evidence this subtask collects. Shown in subtask listings.",
    ),
  type: TemplateTypeSchema.describe(
    "Template-backed evidence type: POLICY, API, CONTAINER, or REPORT.",
  ),
  order: z.number().describe("Display order (0-indexed)"),
  scoringCriteria: z.object({
    instructions: z
      .string()
      .describe(
        "Detailed markdown guidance on HOW to collect this subtask evidence. Required - testers rely on this.",
      ),
    requiredElements: z
      .array(SimplifiedElementSchema)
      .describe("Evidence items to validate for this subtask"),
    qualityThresholds: QualityThresholdsSchema,
  }),
});

export type LLMSubtaskDefinition = z.infer<typeof LLMSubtaskDefinitionSchema>;

/**
 * Main schema for LLM-generated templates
 * Used by sop-parser and control-matrix-converter
 */
export const LLMGeneratedTemplateSchema = z.object({
  title: z.string().describe("Clear, actionable test name"),
  description: z
    .string()
    .describe(
      "Brief summary (1-2 sentences) of WHAT this test verifies. Shown in listings and audit reports.",
    ),
  templateType: TemplateTypeSchema,
  appType: AppTypeSchema.nullish()
    .optional()
    .describe("Application type if identifiable"),
  instructions: z
    .string()
    .describe(
      "Detailed markdown guidance on HOW to collect evidence. Required - testers rely on this for guidance.",
    ),
  requiredElements: z
    .array(SimplifiedElementSchema)
    .describe("Evidence items to validate"),
  qualityThresholds: QualityThresholdsSchema,
  samplingConfig: LLMSamplingConfigSchema.nullable().describe(
    "Population sampling config if applicable",
  ),
  suggestedFrequency: z
    .enum(["ONCE", "MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"])
    .optional(),
  subtasks: z
    .array(LLMSubtaskDefinitionSchema)
    .optional()
    .nullable()
    .describe(
      "For templates requiring multiple distinct types of evidence. Set to null/empty for single-type templates.",
    ),
});

export type LLMGeneratedTemplate = z.infer<typeof LLMGeneratedTemplateSchema>;

// ===== RFC-113: Subtask Definition (for multi-requirement templates) =====
/**
 * Defines a subtask within a template. Used when a single control requires
 * multiple types of evidence (e.g., Policy + Screenshot + System List).
 */
export const SubtaskDefinitionSchema = z.object({
  /** Unique identifier for this subtask within the template */
  id: z.string(),
  /** Display name for the subtask (e.g., "Vendor Inventory") */
  name: z.string().min(1).max(200),
  /** Description of what evidence should be provided */
  description: z.string(),
  /** Type of evidence this subtask collects */
  type: TemplateTypeSchema,
  /** Display order (0-indexed) */
  order: z.number().int().min(0),
  /** Per-subtask scoring criteria */
  scoringCriteria: z.object({
    /** Instructions specific to this subtask */
    instructions: z.string().optional(),
    /** Required elements for this subtask */
    requiredElements: z.array(SimplifiedElementSchema),
    /** Quality thresholds for this subtask */
    qualityThresholds: QualityThresholdsSchema,
  }),
  /** Optional execution config for active template-backed subtasks */
  executionConfig: TemplateExecutionConfigSchema.optional(),
});

export type SubtaskDefinition = z.infer<typeof SubtaskDefinitionSchema>;

// SubtaskSource is exported from generated/enums, no need to re-export

// ===== Scoring Criteria (stored in GuidedTemplate.scoringCriteria) =====
export const ScoringCriteriaSchema = z.object({
  // Per-slot description copied from the framework catalog (e.g. "Evidence
  // of annual review of the board charter"). Snapshotted to
  // ControlEvidenceStatus.description at CES-create time; read directly
  // from the template when the CES column is null.
  description: z.string().optional(),

  // Workflow instructions (markdown) - shown to users and AI
  // Users can write ordered lists, tips, warnings, etc.
  // Template authors can address both human testers and AI in one field
  instructions: z.string().optional(),

  // What to validate (concept-based)
  // For single-requirement templates, this is the main validation list
  // For multi-requirement templates (with subtasks), this can be empty or contain parent-level criteria
  requiredElements: z.array(SimplifiedElementSchema),

  // Quality thresholds
  qualityThresholds: QualityThresholdsSchema,

  // RFC-113: Subtask definitions (when template has multiple requirements)
  // If present and non-empty, this template creates subtasks when instantiated
  subtasks: z.array(SubtaskDefinitionSchema).optional(),
});

export type ScoringCriteria = z.infer<typeof ScoringCriteriaSchema>;

// ===== Compliance Framework DTOs =====
export const ComplianceFrameworkDefDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string().nullable(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  /** Whether the current user can manage (edit/delete) this framework */
  canManage: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ComplianceFrameworkDefDTO = z.infer<
  typeof ComplianceFrameworkDefDTOSchema
>;

export const CreateComplianceFrameworkDefDTOSchema = z.object({
  name: z.string().min(1).max(100),
});

export type CreateComplianceFrameworkDefDTO = z.infer<
  typeof CreateComplianceFrameworkDefDTOSchema
>;

// ===== Control Definition DTOs =====
export const ControlDefDTOSchema = z.object({
  id: z.string(),
  frameworkId: z.string(),
  workspaceId: z.string().nullable(),
  controlId: z.string(),
  /** Controls-tree code (`AUD-02`) for display; never an SC-* id. */
  displayCode: z.string().optional(),
  title: z.string(),
  description: z.string(),
  layer: z.string().nullable().optional(),
  artifactSlug: z.string().nullable().optional(),
  nistMappings: z.array(z.string()).optional().default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ControlDefDTO = z.infer<typeof ControlDefDTOSchema>;

// ===== Template Control Mapping DTOs (many-to-many) =====
export const GuidedTemplateControlMappingDTOSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  controlDefId: z.string(),
  isPrimary: z.boolean(),
  displayOrder: z.number(),
  createdAt: z.date(),
  // Optional nested control info
  controlDef: z
    .object({
      id: z.string(),
      controlId: z.string(),
      /** Controls-tree code (`AUD-02`) for display; never an SC-* id. */
      displayCode: z.string().optional(),
      title: z.string(),
      frameworkId: z.string(),
    })
    .optional(),
});

export type GuidedTemplateControlMappingDTO = z.infer<
  typeof GuidedTemplateControlMappingDTOSchema
>;

export const CreateControlDefDTOSchema = z.object({
  frameworkId: z.string(),
  controlId: z.string().min(1).max(50),
  title: z.string().min(1).max(200),
  description: z.string().min(1),
});

export type CreateControlDefDTO = z.infer<typeof CreateControlDefDTOSchema>;

export const UpdateControlDefDTOSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
});

export type UpdateControlDefDTO = z.infer<typeof UpdateControlDefDTOSchema>;

// ===== Guided Template DTOs =====
export const GuidedTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string().nullable(),
  // RFC-158: `frameworkId` is now derived from the primary control
  // mapping's framework at DTO mapping time (templates span multiple
  // frameworks via controlMappings). Nullable when a template has no
  // mappings yet or when the primary control has no framework.
  frameworkId: z.string().nullable(),
  // RFC-158: the canonical tenancy column now lives directly on the
  // template row. Null for marketplace/global templates.
  workspaceId: z.string().nullable().optional(),
  isPublic: z.boolean(),
  usageCount: z.number(),
  scoringCriteria: ScoringCriteriaSchema,
  currentVersion: z.number(), // Template version number
  author: z.string().nullable(),
  license: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),

  // Display fields (can be derived from doc.topic)
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  taskNumber: z.number(),

  // RFC-151: Runbook discovery layer — denormalized at save time.
  // Empty arrays for non-runbook templates.
  providers: z.array(z.string()).optional(),
  controlRefs: z.array(z.string()).optional(),

  // Control mappings (many-to-many: which controls this template satisfies)
  controlMappings: z.array(GuidedTemplateControlMappingDTOSchema).optional(),

  // ===== RFC-101: Template Marketplace Fields =====
  templateType: TemplateTypeSchema.optional(),
  appType: AppTypeSchema.nullable().optional(),
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(), // RFC-103: typed execution config
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(), // RFC-104: population sampling config

  // Publishing & Attribution
  publisherType: PublisherTypeSchema.optional(),
  publisherUserId: z.string().nullable().optional(),
  publisherTitle: z.string().nullable().optional(),
  publisherOrg: z.string().nullable().optional(),
  publisherOrgId: z.string().nullable().optional(),
  licenseType: LicenseTypeSchema.optional(),

  // Verification
  verifierOrgId: z.string().nullable().optional(),
  verifiedAt: z.date().nullable().optional(),
  verificationBadge: VerificationBadgeSchema.nullable().optional(),

  // Stats
  successRate: z.number().nullable().optional(),
  totalExecutions: z.number().optional(),

  // Clone tracking
  sourceTemplateId: z.string().nullable().optional(),
  clonedByUserId: z.string().nullable().optional(),

  // RFC-158: framework is derived from the primary control mapping's
  // ControlDef.framework at DTO mapping time. Nullable when the template
  // has no mappings yet.
  framework: z
    .object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      workspaceId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  doc: z
    .object({
      id: z.string(),
      topic: z.object({
        id: z.string(),
        title: z.string(),
      }),
    })
    .optional(),

  // Optional publisher user info (for display)
  publisherUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),

  // Optional publisher organization info (for display)
  publisherOrganization: z
    .object({
      id: z.string(),
      name: z.string(),
      logo: z.string().nullable(),
    })
    .nullable()
    .optional(),

  // Optional verifier organization info (for display)
  verifierOrg: z
    .object({
      id: z.string(),
      name: z.string(),
      logo: z.string().nullable(),
    })
    .nullable()
    .optional(),

  // RFC-104: Current version ID for import
  // This is the ID of the GuidedTemplateVersion record (not the version number)
  currentVersionId: z.string().nullable().optional(),

  // ===== RFC-091 Extension: Partner Template Sharing =====
  /** Visibility scope: PUBLIC | ORGANIZATION | PARTNER_CLIENTS | WORKSPACE */
  visibility: z
    .enum(["PUBLIC", "ORGANIZATION", "PARTNER_CLIENTS", "WORKSPACE"])
    .optional(),
  /** Partner organization ID (if visibility=PARTNER_CLIENTS) */
  partnerOrgId: z.string().nullable().optional(),
  /** Client org IDs this is shared with (empty = all clients) */
  sharedWithClientIds: z.array(z.string()).optional(),
  /** Whether clients can clone this template */
  clientsCanClone: z.boolean().optional(),
  /** Whether clients can see the source doc content */
  clientsCanViewSource: z.boolean().optional(),
  /** Partner organization info (for display in client view) */
  partnerOrg: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
});

export type GuidedTemplateDTO = z.infer<typeof GuidedTemplateDTOSchema>;

export const CreateGuidedTemplateDTOSchema = z.object({
  docId: z.string(),
  frameworkId: z.string(),
  scoringCriteria: ScoringCriteriaSchema,
  isPublic: z.boolean().default(false),
  license: z.string().optional(),
});

export type CreateGuidedTemplateDTO = z.infer<
  typeof CreateGuidedTemplateDTOSchema
>;

export const UpdateGuidedTemplateDTOSchema = z.object({
  id: z.string(),
  scoringCriteria: ScoringCriteriaSchema.optional(),
  isPublic: z.boolean().optional(),
  license: z.string().optional(),
  executionConfig: TemplateExecutionConfigSchema.optional(), // RFC-103: Execution steps with pagination
});

export type UpdateGuidedTemplateDTO = z.infer<
  typeof UpdateGuidedTemplateDTOSchema
>;

// ===== RFC-110: Evidence Location Schema (inline for ElementScore) =====
// This is a simplified version - full schema is in evidence-viewer.dto.ts
const ElementEvidenceLocationSchema = z.object({
  artifactType: z.enum([
    "screenshot",
    "evidence_file",
    "text_response",
    "notes",
  ]),
  contentType: z
    .enum(["pdf", "docx", "xlsx", "csv", "image", "text", "json"])
    .optional(),
  screenshotId: z.string().optional(),
  stepId: z.string().optional(),
  evidenceFileId: z.string().optional(),
  chunkId: z.string().optional(),
  page: z.number().optional(),
  section: z.string().optional(),
  sheetName: z.string().optional(),
  columns: z.array(z.string()).optional(),
  rowIndices: z.array(z.number()).optional(),
  cellRange: z.string().optional(),
  textElementId: z.string().optional(),
  noteSnippet: z.string().optional(),
  matchedText: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
});

export const EvidenceScoreResultSchema = z.enum([
  "NO_EVIDENCE",
  "PARTIAL",
  "INSUFFICIENT",
  "SUFFICIENT",
]);

export type EvidenceScoreResult = z.infer<typeof EvidenceScoreResultSchema>;

// ===== Element Score (result of validating one element) =====
export const ElementScoreSchema = z.object({
  elementId: z.string(),
  elementName: z.string(),
  /** Present when this requirement comes from an approved linked policy clause. */
  policyClaim: LinkedPolicyClaimDTOSchema.optional(),
  claimVerdict: z.enum(["supported", "contradicted", "insufficient_evidence"]).optional(),
  isPresent: z.boolean(),
  isValid: z.boolean(),
  confidence: z.number().min(0).max(100),
  reasoningText: z.string(),
  detectedElements: z.array(z.string()),
  suggestions: z.array(z.string()),
  /** How much of this requirement the evidence satisfies (0-1). Distinct from
   * confidence, which measures certainty in the assessment. Optional only for
   * historical score JSON created before partial-credit scoring existed. */
  satisfactionScore: z.number().min(0).max(1).optional(),
  weightedScore: z.number(),
  // RFC-110: Evidence locations for backlinks
  evidenceLocations: z.array(ElementEvidenceLocationSchema).optional(),
});

export type ElementScore = z.infer<typeof ElementScoreSchema>;

// ===== Evidence Score DTOs =====
export const EvidenceScoreDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  templateId: z.string(),
  completenessScore: z.number(),
  accuracyScore: z.number(),
  clarityScore: z.number(),
  complianceScore: z.number(),
  overallScore: z.number(),
  elementScores: z.array(ElementScoreSchema),
  qualityTier: z.enum(["EXCELLENT", "ACCEPTABLE", "NEEDS_IMPROVEMENT", "FAIL"]),
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
  scoredAt: z.date(),
  scoredBy: z.string().nullable(),
  modelUsed: z.string().nullable(),
});

export type EvidenceScoreDTO = z.infer<typeof EvidenceScoreDTOSchema>;

// ===== On-demand Evidence Quality Review (persisted, latest-only) =====
// Distinct from EvidenceSubmission scoring: a non-blocking pre-submission
// quality check the user runs on demand. Mirrors EvidenceQualityReview model.
/** `metadata.artifactKind` of the files an automated (API check) period rollup writes. */
export const AUTOMATED_ROLLUP_ARTIFACT_KINDS = [
  "anchor_rollup_summary",
  "anchor_rollup_runs",
  "anchor_rollup_representative_result",
] as const;
/** `metadata.sourceType` of the files a procedure (runbook) period rollup writes. */
export const PROCEDURE_ROLLUP_SOURCE_TYPE = "procedure_period_rollup";

/**
 * Is this evidence file a period rollup's own artifact — automated or procedure?
 *
 * A rollup carries a defined pass/fail; that IS the verdict (#1907). Its files
 * are evidence for the auditor but are not "evidence to review": they never
 * trigger Vera's review, never count toward "has reviewable evidence", and a
 * test holding nothing else has no AI review card.
 */
export function isPeriodRollupArtifact(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  const m = metadata as Record<string, unknown>;
  if (m.sourceType === PROCEDURE_ROLLUP_SOURCE_TYPE) return true;
  return (
    m.sourceType === "submission_artifact" &&
    (AUTOMATED_ROLLUP_ARTIFACT_KINDS as readonly unknown[]).includes(m.artifactKind)
  );
}

/** `metadata.artifactKind` of the files an auditor sampling round writes onto each test it evidences (RFC-239). */
export const SAMPLING_ROUND_ARTIFACT_KINDS = ["sampling_round_summary", "sampling_round_items"] as const;

/**
 * Does this file carry its own verdict? A period rollup's files (#1907) and an
 * auditor sampling round's (RFC-239) each judge their items against the test's
 * criterion, so they are evidence for the auditor, not evidence for Vera to review.
 */
export function isOwnVerdictArtifact(metadata: unknown): boolean {
  if (isPeriodRollupArtifact(metadata)) return true;
  if (!metadata || typeof metadata !== "object") return false;
  const m = metadata as Record<string, unknown>;
  return (
    m.sourceType === "submission_artifact" &&
    (SAMPLING_ROUND_ARTIFACT_KINDS as readonly unknown[]).includes(m.artifactKind)
  );
}

/** A submission's signed manifest: a signature over the evidence, not evidence to review. */
export function isSubmissionManifest(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  const m = metadata as Record<string, unknown>;
  return m.sourceType === "submission_artifact" && m.artifactKind === "manifest";
}

/** Audit context selected for an advisory Test evidence review. */
export const EvidenceReviewScopeSchema = z.object({
  id: z.string(),
  label: z.string(),
  framework: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
});
export type EvidenceReviewScope = z.infer<typeof EvidenceReviewScopeSchema>;

export const ReviewedArtifactsSnapshotSchema = z.object({
  reviewScope: EvidenceReviewScopeSchema.optional(),
  /**
   * Current evidence files a person supplied — a period rollup's own artifacts
   * are excluded (see {@link isPeriodRollupArtifact}). The rollup deletes and
   * recreates them on every run, and they carry their own pass/fail.
   */
  fileIds: z.array(z.string()),
  docLinkIds: z.array(z.string()),
  screenshotCount: z.number(),
  /**
   * Fingerprint of the requirement the evidence was scored AGAINST (#1838) —
   * the test's checklist, ask, thresholds, template criteria and linked approved claims. Lets a review
   * tell "the requirement changed" apart from "re-assessed". Absent on
   * snapshots stored before this field, which reads as "unknown", not "changed".
   */
  criteriaHash: z.string().optional(),
  /** Allows legacy reviews without a fingerprint to be flagged for newly required claim coverage. */
  policyClaimCount: z.number().int().nonnegative().optional(),
});

/** Why a review's score differs from the one before it (#1838). */
export const EvidenceReviewChangeCauseSchema = z.enum([
  "first",
  "evidence_changed",
  "requirement_changed",
  "reassessed",
]);
export type EvidenceReviewChangeCause = z.infer<typeof EvidenceReviewChangeCauseSchema>;
export type ReviewedArtifactsSnapshot = z.infer<
  typeof ReviewedArtifactsSnapshotSchema
>;

export const EvidenceQualityReviewDTOSchema = z.object({
  id: z.string(),
  statusId: z.string(),
  // The 5 auditor evidence dimensions.
  completenessScore: z.number().nullable(),
  accuracyScore: z.number().nullable(),
  relevanceScore: z.number().nullable(),
  timelinessScore: z.number().nullable(),
  traceabilityScore: z.number().nullable(),
  /** @deprecated Superseded by relevance/timeliness/traceability; non-null only
   *  on reviews run before those dimensions existed. Kept so historical reviews
   *  still render the dimensions they were actually scored on. */
  clarityScore: z.number().nullable(),
  /** @deprecated See clarityScore. */
  complianceScore: z.number().nullable(),
  overallScore: z.number().nullable(),
  scoreResult: EvidenceScoreResultSchema,
  qualityTier: z
    .enum(["EXCELLENT", "ACCEPTABLE", "NEEDS_IMPROVEMENT", "FAIL"])
    .nullable(),
  elementScores: z.array(ElementScoreSchema).nullable(),
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
  reviewedArtifacts: ReviewedArtifactsSnapshotSchema.nullable(),
  reviewedBy: z.string().nullable(),
  /** Display name (or email) of who ran the review. */
  reviewedByName: z.string().nullable(),
  reviewedAt: z.date(),
  /** True when the test's current evidence set differs from reviewedArtifacts. */
  stale: z.boolean(),
  /** True when the requirement was edited since this review scored against it. */
  requirementChanged: z.boolean().optional(),
  /** The model was unsure of its own reading. A flag on the assessment — never
   *  a deduction from the score (#1838). */
  uncertain: z.boolean().optional(),
  /** Set on a Re-run that found nothing to re-score: same evidence, same
   *  requirement, so the stored review IS the answer. */
  unchanged: z.boolean().optional(),
  /** The review before this one, and why this one differs. */
  previous: z
    .object({
      overallScore: z.number().nullable(),
      qualityTier: z.string().nullable(),
      reviewedAt: z.date(),
      /** Null when this review predates cause tracking. */
      cause: EvidenceReviewChangeCauseSchema.nullable(),
    })
    .nullable()
    .optional(),
});

export type EvidenceQualityReviewDTO = z.infer<
  typeof EvidenceQualityReviewDTOSchema
>;

// Import EvidenceFrequency from generated enums (RFC-104)
import { EvidenceFrequencySchema, EvidenceCategorySchema } from "../generated/enums";
export type { EvidenceFrequency } from "../generated/enums";
export { EvidenceCategorySchema } from "../generated/enums";
export type { EvidenceCategory } from "../generated/enums";

// ===== RFC-104: Export Metadata schema =====
export const ExportMetadataSchema = z.object({
  /** What company does (for export only) */
  controlNarrative: z.string().optional(),
  /** Additional auditor notes */
  statusNotes: z.string().optional(),
  /** Test deactivation metadata for v1 flows that avoid schema churn. */
  deactivation: z
    .object({
      mode: z.enum(['NOT_APPLICABLE', 'ON_HOLD']),
      reason: z.string(),
      deactivatedBy: z.string().nullable().optional(),
      deactivatedAt: z.string(),
      previousStatus: ControlEvidenceStatusEnumSchema.optional(),
    })
    .optional(),
  /** Future: auditor-defined fields */
  customFields: z.record(z.string()).optional(),
});

export type ExportMetadata = z.infer<typeof ExportMetadataSchema>;

/** RFC-224 necessity ladder, most-inclusive first. Mirrors
 *  `ScreenataControl["necessity"]` and `SlotNecessity` on the server — the
 *  Baseline gate keeps `required` + `common-ask` active and defers the rest. */
/** RFC-234 — how a Test is included: owed everywhere, waits on a workspace
 *  fact, or depth beyond a first audit. Read from the tuple at read time. */
export const TestInclusionSchema = z.enum(['core', 'scope', 'extended']);
export type TestInclusion = z.infer<typeof TestInclusionSchema>;

/** RFC-234 M3 — how the evidence is collected, derived server-side
 *  (`resolveCollection`): a runnable bound check, an operator attestation, a
 *  policy document, or a human upload. Replaces reading `templateType`. */
export const TestCollectionSchema = z.enum(['automated', 'policy_link', 'attestation', 'screenshot', 'upload']);
export type TestCollection = z.infer<typeof TestCollectionSchema>;

export const TestEvidencePreparationSchema = z.enum(["NEEDS_WORK", "SUBMITTED", "MONITORING", "LEGACY_ACCEPTED"]);
export type TestEvidencePreparation = z.infer<typeof TestEvidencePreparationSchema>;

// ===== Control Evidence Status DTOs (RFC-097, RFC-104) =====
export const ControlEvidenceStatusDTOSchema = z.object({
  workflowVersion: z.number().optional(),
  effectiveOwnerRoleId: z.string().nullable().optional(),
  ownerInherited: z.boolean().optional(),
  ownerConflict: z.boolean().optional(),
  preparation: TestEvidencePreparationSchema.optional(),
  id: z.string(),
  workspaceId: z.string(),
  controlDefId: z.string(),
  // RFC-214: framework requirements satisfied by this test's evidence slot.
  // Optional during the additive rollout; current readers always return an array.
  frameworkCitations: z.array(EvidenceSlotCitationDTOSchema).optional(),
  templateId: z.string().nullable(), // RFC-097: One status per test per control (nullable for migration)
  workflowId: z.string().nullable().optional(), // RFC-167: Workflow-backed Runbook binding
  evidenceDocId: z.string().nullable(),
  status: ControlEvidenceStatusEnumSchema,
  priority: PrioritySchema,
  latestScore: z.number().nullable(),
  latestQualityTier: z.string().nullable(),
  assignedTo: z.string().nullable(),
  // Assigned user details (for display)
  assignedUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      image: z.string().nullable(),
      isServiceAccount: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  // RFC-202: role-typed Owner/Approver override (null = inherit from control).
  ownerRoleId: z.string().nullable().optional(),
  approverRoleId: z.string().nullable().optional(),
  // RFC-202: the people the Approver role currently resolves to (CES override →
  // linked control, no workspace-owner floor). Populated only by single-test
  // fetches (getStatusById); drives the approver-override review gate.
  resolvedApproverUserIds: z.array(z.string()).optional(),
  resolvedApproverName: z.string().nullable().optional(),
  // Work queue fields
  dueDate: z.date().nullable(),
  externalTicketUrl: z.string().nullable(),
  externalTicketId: z.string().nullable(),
  externalTicketSource: z.string().nullable(),
  activeSubmissionId: z.string().nullable(),
  slaAggregate: SlaAggregateDTOSchema.nullable().optional(),
  slaItems: z.array(SlaEvaluatedItemDTOSchema).optional(),

  // RFC-104: N/A handling
  naReason: z.string().nullable().optional(),
  naApprovedBy: z.string().nullable().optional(),
  naApprovedAt: z.date().nullable().optional(),

  // RFC-224 program depth — both fields are DERIVED at read time (no column,
  // no backfill) so they can never drift from the catalog.
  /** RFC-234 — the tuple's inclusion, read at read time from the published
   *  catalog: `core` is owed everywhere, `scope` waits on a workspace fact,
   *  `extended` is depth beyond a first audit (Enterprise-ready). Shown as a
   *  chip in every workspace so a customer can see and hand-prune. */
  inclusion: TestInclusionSchema.optional(),
  /** For `scope`: the phrase completing "applies if …". */
  scopeFactLabel: z.string().nullable().optional(),
  /** RFC-194 §4.6: 'MANUAL' when a person pinned this Test's scope by hand. */
  scopeSource: z.string().nullable().optional(),
  collection: TestCollectionSchema.optional(),
  /** Screenata can generate this test's evidence from its own data (the
   *  tuple's built-in report, e.g. policy acknowledgments from People). */
  hasBuiltInReport: z.boolean().optional(),
  /** ACTIVE | DEFERRED | NOT_APPLICABLE. DEFERRED is NOT_APPLICABLE carrying a
   *  deferral marker: a roadmap item ("later"), not a verdict ("not for you").
   *  The distinction is what keeps a lighter default from reading as a claim
   *  that the deferred work never applies. */
  effectiveScopeState: z
    .enum(['ACTIVE', 'DEFERRED', 'NOT_APPLICABLE'])
    .optional(),
  /** Why it is deferred, when it is. */
  deferralKind: z
    .enum(['SCOPE', 'EXTENDED', 'AUDIT_TYPE', 'MANUAL'])
    .nullable()
    .optional(),
  deferralReason: z.string().nullable().optional(),

  // RFC-104: Non-performing control handling
  nonPerformingReason: z.string().nullable().optional(),
  designEvidenceDocId: z.string().nullable().optional(),

  // RFC-104: Evidence scheduling
  frequency: EvidenceFrequencySchema.nullable().optional(),
  nextDueDate: z.date().nullable().optional(),
  reminderSentAt: z.date().nullable().optional(),

  // RFC-104: Export metadata
  exportMetadata: ExportMetadataSchema.nullable().optional(),

  // RFC-104: Population for sampling workflow (can be set before sample is created)
  populationId: z.string().nullable().optional(),
  population: z
    .object({
      id: z.string(),
      name: z.string(),
      type: z.string(),
      recordCount: z.number(),
      fileUrl: z.string().optional(),
      filename: z.string().optional(),
    })
    .nullable()
    .optional(),

  // RFC-104: Sample data for SAMPLING tests
  sampleId: z.string().nullable().optional(),
  sample: z
    .object({
      id: z.string(),
      populationId: z.string(),
      method: z.string(),
      sampleSize: z.number(),
      seed: z.number().nullable(),
      selectedIndices: z.array(z.number()),
      selectedAt: z.date(),
      passedCount: z.number().nullable(),
      failedCount: z.number().nullable(),
      pendingCount: z.number().nullable(),
      population: z
        .object({
          id: z.string(),
          name: z.string(),
          type: z.string(),
          recordCount: z.number(),
        })
        .optional(),
      items: z
        .array(
          z.object({
            id: z.string(),
            rowIndex: z.number(),
            identifier: z.string(),
            status: z.string(),
            passed: z.boolean().nullable(),
          }),
        )
        .optional(),
    })
    .nullable()
    .optional(),

  // RFC-107: Subtask support for per-item sampling evidence
  parentId: z.string().nullable().optional(),
  // Parent context (for displaying back link when viewing subtask)
  parent: z
    .object({
      id: z.string(),
      templateId: z.string().nullable(),
      title: z.string().nullable(),
    })
    .nullable()
    .optional(),
  sampleItemId: z.string().nullable().optional(),
  // Sample item context (for displaying sample row data when viewing subtask)
  sampleItem: z
    .object({
      id: z.string(),
      rowIndex: z.number(),
      identifier: z.string(),
      rowData: z.unknown(),
    })
    .nullable()
    .optional(),
  dynamicParams: z.record(z.unknown()).nullable().optional(),

  // RFC-113: Requirement-based subtask fields
  subtaskDefId: z.string().nullable().optional(),
  subtaskSource: SubtaskSourceSchema.nullable().optional(),
  // RFC-113: Direct title field (for subtasks: from definition or sample item; for tasks: custom override)
  title: z.string().nullable().optional(),
  // Subtask definition details (populated when viewing a subtask created from template definition)
  subtaskDefinition: SubtaskDefinitionSchema.nullable().optional(),

  // Subtask summary (for parent test with subtasks)
  subTaskCount: z.number().optional(),
  subTaskCompletedCount: z.number().optional(),
  hasSubTasks: z.boolean().optional(),

  evidenceCategory: EvidenceCategorySchema.nullable().optional(),
  // Template type for UI rendering. The DB column is the TemplateType enum;
  // typing it as the enum (not z.string()) is what lets kind derivation
  // (lib/test-kind.ts) accept this DTO without a cast.
  templateType: TemplateTypeSchema.nullable().optional(),

  // Test-level fields (copied from template at creation time, can be edited independently)
  // These are the test's OWN data, not the template's current data
  description: z.string().nullable().optional(),
  appType: z.string().nullable().optional(),
  instructions: z.string().nullable().optional(),
  requiredElements: z.array(SimplifiedElementSchema).nullable().optional(),
  qualityThresholds: QualityThresholdsSchema.nullable().optional(),
  // RFC-113: Subtask definitions for CONTAINER templates
  subtasks: z.array(SubtaskDefinitionSchema).nullable().optional(),
  // Sampling configuration (for population-based tests)
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(),
  // Execution configuration for active template-backed tests
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(),
  // Issue #972 — auditor-grade evidence guidance ("what your auditor will accept")
  auditorGuidance: AuditorEvidenceGuidanceSchema.nullable().optional(),
  // RFC-234 — "how your auditor judges it", snapshotted from the canonical tuple
  auditorAsk: z.string().nullable().optional(),

  // Timestamps
  lastTestedAt: z.date().nullable(),
  retestCadenceDays: z.number().int().nullable().optional(),
  checkFrequencyDays: z.number().int().nullable().optional(),
  checkCron: z.string().nullable().optional(),
  checkInputs: z.record(z.unknown()).nullable().optional(),
  nextRetestAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type ControlEvidenceStatusDTO = z.infer<
  typeof ControlEvidenceStatusDTOSchema
>;

export const UpdateControlEvidenceStatusDTOSchema = z.object({
  id: z.string(),
  status: ControlEvidenceStatusEnumSchema.optional(),
  priority: PrioritySchema.optional(),
  assignedTo: z.string().nullable().optional(),
  dueDate: z.date().nullable().optional(),
  externalTicketUrl: z.string().nullable().optional(),
  externalTicketId: z.string().nullable().optional(),
  externalTicketSource: z.string().nullable().optional(),

  // RFC-104: New updatable fields
  naReason: z.string().nullable().optional(),
  nonPerformingReason: z.string().nullable().optional(),
  designEvidenceDocId: z.string().nullable().optional(),
  frequency: EvidenceFrequencySchema.nullable().optional(),
  nextDueDate: z.date().nullable().optional(),
  exportMetadata: ExportMetadataSchema.nullable().optional(),
  // RFC-104: Population link (for sampling workflow)
  populationId: z.string().nullable().optional(),
});

export type UpdateControlEvidenceStatusDTO = z.infer<
  typeof UpdateControlEvidenceStatusDTOSchema
>;

// ===== Additional File (for evidence submissions) =====
export const AdditionalFileDTOSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
  uploadedAt: z.date(),
  mimeType: z.string(),
  size: z.number(), // bytes
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type AdditionalFileDTO = z.infer<typeof AdditionalFileDTOSchema>;

export const EvidenceSubmissionArtifactSnapshotItemSchema = z.object({
  id: z.string().optional(),
  kind: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  documentType: z.string().optional(),
  documentId: z.string().optional(),
  versionId: z.string().nullable().optional(),
  documentTitle: z.string().optional(),
  contentHash: z.string().optional(),
  reviewRecords: z.array(VaultDocumentReviewRecordSchema).optional(),
  documentUrl: z.string().optional(),
  programId: z.string().optional(),
  policySlug: z.string().optional(),
  policyVersionId: z.string().optional(),
  policyVersion: z.number().nullable().optional(),
  systemDescriptionVersion: z.number().nullable().optional(),
  generatedAt: z.string().nullable().optional(),
  approvedAt: z.string().nullable().optional(),
  filename: z.string().optional(),
  url: z.string().optional(),
  mimeType: z.string().nullable().optional(),
  size: z.number().nullable().optional(),
  uploadedAt: z.string().optional(),
  linkedAt: z.string().optional(),
});

/**
 * Refs to the auditor narrative + signed manifest that anchor an API-run
 * evidence submission. Optional on the snapshot because pre-narrative
 * submissions exist and the manual-evidence path doesn't produce them.
 */
export const EvidenceSubmissionNarrativeRefSchema = z.object({
  url: z.string(),
  sha256: z.string(),
  templateVersion: z.string(),
});

export const EvidenceSubmissionManifestRefSchema = z.object({
  url: z.string(),
  sha256: z.string(),
  signatureFingerprint: z.string().nullable().optional(),
  signedAt: z.string().nullable().optional(),
});

export const EvidenceSubmissionArtifactSnapshotSchema = z.object({
  version: z.literal(1),
  artifacts: z.array(EvidenceSubmissionArtifactSnapshotItemSchema),
  narrative: EvidenceSubmissionNarrativeRefSchema.optional(),
  manifest: EvidenceSubmissionManifestRefSchema.optional(),
});

export type EvidenceSubmissionNarrativeRefDTO = z.infer<
  typeof EvidenceSubmissionNarrativeRefSchema
>;
export type EvidenceSubmissionManifestRefDTO = z.infer<
  typeof EvidenceSubmissionManifestRefSchema
>;
export type EvidenceSubmissionArtifactSnapshotDTO = z.infer<
  typeof EvidenceSubmissionArtifactSnapshotSchema
>;

// ===== Evidence Submission DTOs (RFC-097) =====
export const PeriodRollupSnapshotDTOSchema = z.object({
  latestObservationAt: z.string().nullable().optional(),
  observedDays: z.number().optional(),
  passingDays: z.number().optional(),
  trailingGapDays: z.number().optional(),
  collectionErrorCount: z.number().optional(),
  limitations: z.array(z.string()).optional(),
  computedAt: z.string(),
  evidenceFromDate: z.string(),
  evidenceToDate: z.string(),
  runCount: z.number(),
  successCount: z.number(),
  successRate: z.number(),
  coveredDays: z.number(),
  gapDays: z.number(),
  anomalyCount: z.number(),
});
export type PeriodRollupSnapshotDTO = z.infer<
  typeof PeriodRollupSnapshotDTOSchema
>;

export const EvidenceSubmissionDTOSchema = z.object({
  workflowVersion: z.number().optional(),
  id: z.string(),
  statusId: z.string(),
  docId: z.string().nullable(), // Nullable for subtasks that use EvidenceFile instead
  topicId: z.string().optional(), // Topic ID for navigation
  topicTitle: z.string().optional(), // Topic title for breadcrumb display
  // Template version tracking
  templateVersionId: z.string().nullable(), // Reference to GuidedTemplateVersion
  templateVersion: z.number().nullable(), // Version number (1, 2, 3...)
  templateCurrentVersion: z.number().nullable(), // Current template version for comparison
  submittedBy: z.string().optional(), // Nullable for guest submissions
  submittedByUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),
  submittedAt: z.date(),
  revisionNumber: z.number(),
  // Guest submission tracking
  submittedByEmail: z.string().optional(), // Email from share link (for guests)
  shareToken: z.string().optional(), // Share token used to create submission
  // Scores — the 5 auditor evidence dimensions (+ 2 deprecated, see
  // EvidenceQualityReviewDTOSchema).
  completenessScore: z.number().nullable(),
  accuracyScore: z.number().nullable(),
  relevanceScore: z.number().nullable(),
  timelinessScore: z.number().nullable(),
  traceabilityScore: z.number().nullable(),
  clarityScore: z.number().nullable(),
  complianceScore: z.number().nullable(),
  overallScore: z.number().nullable(),
  scoreResult: EvidenceScoreResultSchema,
  qualityTier: z
    .enum(["EXCELLENT", "ACCEPTABLE", "NEEDS_IMPROVEMENT", "FAIL"])
    .nullable(),
  // Element-by-element scores with LLM reasoning
  elementScores: z.array(ElementScoreSchema).nullable(),
  // AI feedback and recommendations
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
  // Additional files
  additionalFiles: z.array(AdditionalFileDTOSchema).nullable(),
  artifactSnapshot: EvidenceSubmissionArtifactSnapshotSchema.nullable().optional(),
  // Review workflow
  reviewStatus: EvidenceReviewStatusSchema,
  reviewedBy: z.string().nullable(),
  reviewedByUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      email: z.string().nullable(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),
  reviewedAt: z.date().nullable(),
  comments: z.string().nullable(),
  // Override
  overrideReason: z.string().nullable(),
  overrideUsed: z.boolean(),
  // Ad-hoc conclusion extracted from doc content (for ad-hoc evidence without AI score)
  adHocConclusion: z.string().nullable().optional(),
  // Source execution (for automated evidence)
  sourceExecutionId: z.string().nullable(),
  // Period-based evidence aggregation
  evidenceFromDate: z.date().nullable(),
  evidenceToDate: z.date().nullable(),
  // Rollup statistics (null for single/manual evidence)
  runCount: z.number().nullable(),
  successCount: z.number().nullable(),
  successRate: z.number().nullable(),
  coveredDays: z.number().nullable(),
  gapDays: z.number().nullable(),
  anomalyCount: z.number().nullable(),
  executionIdsJson: z.array(z.string()).nullable(),
  rollupComputedAt: z.date().nullable(),
  // RFC-208: per-covering-check breakdown for anchor period rollups
  rollupBreakdown: z
    .array(
      z.object({
        statusId: z.string(),
        apiCheckKey: z.string().nullable(),
        runCount: z.number(),
        passCount: z.number(),
        lastRunAt: z.string().nullable(),
        lastResult: z.string().nullable(),
        latestPassRunId: z.string().nullable(),
        // #1444: latest run regardless of verdict — the period-coverage source
        // when the check has no PASS in the period. Optional: rows persisted
        // before #1444 have no such key.
        latestRunId: z.string().nullable().optional(),
        // #1425: provider-side history window examined by this check's
        // representative run (can predate the rollup's own run window).
        coveredFrom: z.string().optional(),
        coveredTo: z.string().optional(),
        coverageSource: z.string().optional(),
      }),
    )
    .nullable()
    .optional(),
  // Evidence pack (when generated via submitForReview)
  evidencePack: z
    .object({
      id: z.string(),
      storageUrl: z.string().nullable(),
      replayVideoUrl: z.string().nullable(),
      topicTitle: z.string().nullable(),
      applicableControls: z.array(z.string()),
    })
    .nullable()
    .optional(),
  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type EvidenceSubmissionDTO = z.infer<typeof EvidenceSubmissionDTOSchema>;

// RFC-113: Text response for TEXT type required elements
export const TextResponseSchema = z.object({
  requirementId: z.string(),
  value: z.string(),
});
export type TextResponse = z.infer<typeof TextResponseSchema>;

export const CreateEvidenceSubmissionDTOSchema = z.object({
  statusId: z.string(),
  docId: z.string().optional(), // Optional for subtasks that use EvidenceFile instead
  templateVersionId: z.string().optional(), // Optional - will use current template version if not provided
  additionalFiles: z.array(AdditionalFileDTOSchema).optional(),
  // RFC-113: Text responses for TEXT type required elements (e.g., tester conclusions)
  textResponses: z.array(TextResponseSchema).optional(),
  // Guest submission fields
  submittedBy: z.string().optional(), // User ID (for WorkspaceReader guests)
  submittedByEmail: z.string().email().optional(), // Email from share link
  shareToken: z.string().optional(), // Share token used
  // Quality gate override
  overrideValidation: z.boolean().optional(), // User chose to submit despite failing quality gate
  // Source execution (for automated evidence)
  sourceExecutionId: z.string().optional(),
  apiRunId: z.string().optional(),
  runbookExecutionId: z.string().optional(),
  // Period-based evidence aggregation (optional - null for single/manual)
  evidenceFromDate: z.date().optional(),
  evidenceToDate: z.date().optional(),
});

export type CreateEvidenceSubmissionDTO = z.infer<
  typeof CreateEvidenceSubmissionDTOSchema
>;

export const UpdateEvidenceSubmissionDTOSchema = z.object({
  id: z.string(),
  reviewStatus: EvidenceReviewStatusSchema.optional(),
  comments: z.string().nullable().optional(),
  overrideReason: z.string().nullable().optional(),
  overrideUsed: z.boolean().optional(),
  additionalFiles: z.array(AdditionalFileDTOSchema).optional(),
});

export type UpdateEvidenceSubmissionDTO = z.infer<
  typeof UpdateEvidenceSubmissionDTOSchema
>;

// ===== Template for Recording (sent to extension) =====
export const TemplateForRecordingDTOSchema = z.object({
  templateId: z.string(),
  controlTitle: z.string(),
  controlDescription: z.string(),
  controlId: z.string().optional(), // Control ID (e.g., "AC-1.2.3")
  frameworkName: z.string().optional(), // Framework name (e.g., "SOC 2 Type II")
  instructions: z.string().optional(), // Workflow instructions (markdown) for testers and AI
  /** Resolved server-side — see the note on `TaskDTO.evidenceChecklist`. */
  evidenceChecklist: z.array(SimplifiedElementSchema),
  qualityThresholds: QualityThresholdsSchema.optional(), // Quality targets
  // #972 guidance, read off the CES being recorded. The recorder is the one
  // surface with no route back to the test detail page, so it has to carry the
  // acceptable artifacts + judging dimensions itself.
  auditorGuidance: AuditorEvidenceGuidanceSchema.nullable().optional(),
  // RFC-234 — "how your auditor judges it", snapshotted from the canonical tuple
  auditorAsk: z.string().nullable().optional(),
});

export type TemplateForRecordingDTO = z.infer<
  typeof TemplateForRecordingDTOSchema
>;

// ===== Validation Result (LLM structured output) =====
export const ValidationResultSchema = z.object({
  isValid: z.boolean(),
  confidence: z.number().min(0).max(100),
  reasoningText: z.string(),
  detectedElements: z.array(z.string()),
  suggestions: z.array(z.string()).optional(),
});

export type ValidationResult = z.infer<typeof ValidationResultSchema>;

// ===== Task Control DTO (nested control info for TaskDTO) =====
export const TaskControlDTOSchema = z.object({
  id: z.string(),
  controlId: z.string(), // e.g., "CC6.1"
  /** Controls-tree code (`AUD-02`) for display; never an SC-* id. */
  displayCode: z.string().optional(),
  title: z.string(),
  frameworkId: z.string(),
  frameworkName: z.string().nullable(),
});
export type TaskControlDTO = z.infer<typeof TaskControlDTOSchema>;

/**
 * Latest automated API-check health for an API `test`. This is the check's
 * verdict on the last run (a fact), kept DELIBERATELY separate from the CES
 * compliance/submission status — a red or errored check does NOT change where
 * the test sits in the review workflow (RFC-189). `RUNNING` covers a queued/
 * in-flight run that has no verdict yet.
 */
export const ApiCheckHealthSchema = z.enum([
  'PASS',
  'FAIL',
  'INCONCLUSIVE',
  'ERROR',
  'RUNNING',
]);
export type ApiCheckHealth = z.infer<typeof ApiCheckHealthSchema>;

export const LatestApiCheckResultDTOSchema = z.object({
  hasEvidence: z.boolean(),
  health: ApiCheckHealthSchema.nullable(),
  startedAt: z.date(),
});
export type LatestApiCheckResultDTO = z.infer<typeof LatestApiCheckResultDTOSchema>;

/** Current accepted covering-check progress for a requirement anchor. */
export const AutomationRollupSchema = z.object({
  passed: z.number().int().nonnegative(),
  total: z.number().int().positive(),
});
export type AutomationRollup = z.infer<typeof AutomationRollupSchema>;

// ===== Task DTO (RFC-098: Linear-style task list) =====
// Unified DTO for both "My Tasks", "Review Queue", and "Control Tree" views
// Also used directly by StatusRow component
export const TaskDTOSchema = z.object({
  workflowVersion: z.number().optional(),
  preparation: TestEvidencePreparationSchema.optional(),
  // From ControlEvidenceStatus (the "task")
  statusId: z.string(),
  // RFC-193: the requirement anchor this covering check rolls up to (null = not a check).
  coversAnchorId: z.string().nullable().optional(),
  // Proof role of this check inside the anchor's evidence bundle.
  checkRole: z.enum(["REQUIRED", "SUPPORTING"]).nullable().optional(),
  // RFC-193: derived requirement state when this task IS an anchor (else null).
  anchorState: z
    .enum([
      "NOT_APPLICABLE",
      "NO_APPLICABLE_RESOURCES",
      "SATISFIED_WITH_EXCEPTION",
      "AUTOMATION_CONTRADICTS",
      "MANUALLY_SATISFIED",
      "VERIFIED_AUTOMATICALLY",
      "EVIDENCE_READY_FOR_REVIEW",
      "AUTOMATION_AVAILABLE",
      "NEEDS_MANUAL_EVIDENCE",
    ])
    .nullable()
    .optional(),
  automationRollup: AutomationRollupSchema.optional(),
  createdAt: z.date().optional(),
  assignedTo: z.string().nullable(),
  dueDate: z.date().nullable(),
  frequency: EvidenceFrequencySchema.nullable().optional(),
  retestCadenceDays: z.number().int().nullable().optional(),
  checkFrequencyDays: z.number().int().nullable().optional(),
  checkCron: z.string().nullable().optional(),
  checkInputs: z.record(z.unknown()).nullable().optional(),
  nextRetestAt: z.date().nullable().optional(),
  nextDueDate: z.date().nullable().optional(),
  externalTicketUrl: z.string().nullable(),
  lastTestedAt: z.date().nullable().optional(), // When test was last run
  slaAggregate: SlaAggregateDTOSchema.nullable().optional(),
  slaItems: z.array(SlaEvaluatedItemDTOSchema).optional(),

  // From GuidedTemplate (nullable for ad-hoc recordings)
  templateId: z.string().nullable(),
  templateType: z
    .enum(["POLICY", "API", "CONTAINER", "REPORT"])
    .nullable()
    .optional(),
  evidenceCategory: EvidenceCategorySchema.nullable().optional(),
  topicId: z.string().nullable(), // For navigation to evidence detail page
  displayId: z.string(), // e.g., "TEST-001"
  title: z.string(), // From template.doc.topic.title or "Ad-hoc Evidence"
  searchTexts: z.array(z.string()).optional(),
  isAdHoc: z.boolean(), // True if no template (ad-hoc recording)

  // Controls associated with this task (P1e: multi-control support)
  controls: z.array(TaskControlDTOSchema),
  // RFC-214: direct CES requirement mapping, derived through EvidenceSlotDef.
  frameworkCitations: z.array(EvidenceSlotCitationDTOSchema).optional(),
  // RFC-215: requirement demand is orthogonal to the CES workflow status.
  // Populated when a list includes citation-derived N/A Tests.
  frameworkDemand: z
    .enum(["ACTIVE", "OUT_OF_SCOPE", "UNMAPPED"])
    .optional(),

  // User-facing rollup: citation scope can make a Test effectively N/A without
  // overwriting its durable execution status.
  effectiveStatus: ControlEvidenceStatusEnumSchema.optional(),

  // Status from ControlEvidenceStatus
  status: ControlEvidenceStatusEnumSchema,

  // Intrinsic test priority (per-workspace; seeded from blueprint catalog
  // for Vanta-style quick-wins, MEDIUM otherwise). Distinct from due-date
  // urgency — see web/src/pages/tasks/taskPriority.ts for the legacy
  // derived priority that this is gradually replacing.
  priority: PrioritySchema,

  // From EvidenceSubmission (if exists)
  submission: z
    .object({
      id: z.string(),
      reviewStatus: EvidenceReviewStatusSchema,
      overallScore: z.number().nullable(),
      qualityTier: z
        .enum(["EXCELLENT", "ACCEPTABLE", "NEEDS_IMPROVEMENT", "FAIL"])
        .nullable(),
      reviewerComments: z.string().nullable(),
      submittedAt: z.date(),
      revisionNumber: z.number().optional(),
      executionId: z.string().nullable().optional(), // Source execution for navigation
    })
    .nullable(),

  // Assignee info (for display)
  assignee: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string().nullable().optional(),
      image: z.string().nullable(),
      isServiceAccount: z.boolean().optional(),
    })
    .nullable(),

  // Submitter info (for review queue - who submitted the evidence)
  submitter: z
    .object({
      id: z.string(),
      name: z.string(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),

  // Reviewer info (for reviewed submissions)
  reviewer: z
    .object({
      id: z.string(),
      name: z.string(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),
  reviewedAt: z.date().nullable().optional(),

  // RFC-107: Subtask stats for sampling tests (parent tasks with subtasks)
  subtaskStats: z
    .object({
      total: z.number(),
      completed: z.number(),
      inProgress: z.number(),
      notStarted: z.number(),
    })
    .nullable()
    .optional(),

  // RFC-107: Sampling workflow indicator
  // true = has sample with subtasks, false = needs sample/subtasks, null = not a sampling test
  hasSample: z.boolean().nullable().optional(),

  // Test instructions (stored directly in ControlEvidenceStatus)
  // For subtasks: copied from subtaskDef.scoringCriteria at creation
  // For parent tasks: copied from template.scoringCriteria at creation
  instructions: z.string().nullable().optional(),
  /**
   * Resolved via `resolveEvidenceChecklist` server-side — CES-over-template
   * precedence already applied, advisory elements already dropped. The raw
   * `requiredElements` list is deliberately NOT exposed here: consumers that
   * reach for it end up re-deriving precedence and forgetting the filter.
   * Authoring surfaces use the template DTOs instead.
   */
  evidenceChecklist: z.array(SimplifiedElementSchema).optional(),
  qualityThresholds: QualityThresholdsSchema.nullable().optional(),
  // Issue #972 — auditor-grade evidence guidance ("what your auditor will accept")
  auditorGuidance: AuditorEvidenceGuidanceSchema.nullable().optional(),
  // RFC-234 — "how your auditor judges it", snapshotted from the canonical tuple
  auditorAsk: z.string().nullable().optional(),

  // Labels assigned to this task
  labels: z.array(LabelDTOSchema).optional(),

  // For API tests: latest automated check verdict (PASS/FAIL/INCONCLUSIVE/ERROR/
  // RUNNING), surfaced as a health signal alongside — not merged into — the CES
  // submission status. Null/absent for non-API tests. See ApiCheckHealthSchema.
  latestApiRunStatus: ApiCheckHealthSchema.nullable().optional(),
  /** Actual monitoring run date, independent of a historical evidence review. */
  latestApiRunAt: z.date().nullable().optional(),
  /** Completed missing collection days since the latest observation. */
  monitoringTrailingGapDays: z.number().int().nonnegative().optional(),

  // Count of open auditor requests anchored to this test (unresolved
  // EVIDENCE_REQUEST / CLARIFICATION / EXCEPTION threads). Drives the
  // Tasks board card badge. See OpenRequestDTO.
  openRequestCount: z.number().optional(),

  // RFC-234 inclusion — derived at read time from the published catalog and
  // the deferral marker; see ControlEvidenceStatusDTOSchema for the full
  // contract. Carried on the row DTO so a list can show the chips without a
  // second fetch.
  inclusion: TestInclusionSchema.optional(),
  scopeFactLabel: z.string().nullable().optional(),
  collection: TestCollectionSchema.optional(),
  deferralKind: z
    .enum(['SCOPE', 'EXTENDED', 'AUDIT_TYPE', 'MANUAL'])
    .nullable()
    .optional(),
  deferralReason: z.string().nullable().optional(),
});

export type TaskDTO = z.infer<typeof TaskDTOSchema>;

/** Read-only workflow projection for Test filters/counts; never writes approval. */
export function testPreparationStatusOf(test: {
  status?: string | null;
  effectiveStatus?: string | null;
  preparation?: TestEvidencePreparation;
  latestApiRunStatus?: ApiCheckHealth | null;
}): string | undefined {
  const status = test.effectiveStatus ?? test.status ?? undefined;
  if (status === 'NOT_APPLICABLE' || !test.preparation) return status;
  if (test.preparation === 'MONITORING') {
    switch (test.latestApiRunStatus) {
      case 'PASS': return 'APPROVED';
      case 'FAIL': return 'FAILED';
      case 'ERROR':
      case 'INCONCLUSIVE': return 'INCONCLUSIVE';
      default: return 'IN_PROGRESS';
    }
  }
  if (test.preparation === 'LEGACY_ACCEPTED') return 'APPROVED';
  if (test.preparation === 'SUBMITTED') return 'SUBMITTED';
  return 'NOT_STARTED';
}

/** Complete search/kind/inclusion candidates for status cards, without list details. */
export const TaskStatCandidateDTOSchema = TaskDTOSchema.pick({
  status: true, effectiveStatus: true, title: true, displayId: true, searchTexts: true, controls: true,
  inclusion: true, collection: true, templateType: true, automationRollup: true,
  preparation: true, latestApiRunStatus: true,
}).extend({ assignee: z.object({ name: z.string() }).nullable() });
export type TaskStatCandidateDTO = z.infer<typeof TaskStatCandidateDTOSchema>;

// ===== Get My Tasks Input =====
export const TaskSortBySchema = z.enum(["dueDate", "createdAt"]);
export type TaskSortBy = z.infer<typeof TaskSortBySchema>;

export const ListAllTestsSortBySchema = z.enum([
  "manual",
  "priority",
  "dueDate",
  "createdDate",
  "title",
]);

export const SortOrderSchema = z.enum(["asc", "desc"]);
export type SortOrder = z.infer<typeof SortOrderSchema>;

export const GetMyTasksInputSchema = z.object({
  status: z.array(ControlEvidenceStatusEnumSchema).optional(),
  frameworkId: z.string().optional(),
  search: z.string().optional(),
  sortBy: TaskSortBySchema.optional().default("dueDate"),
  sortOrder: SortOrderSchema.optional().default("asc"),
  labelIds: z.array(z.string()).optional(),
  assigneeOnly: z.boolean().optional(),
  templateType: TemplateTypeSchema.optional(),
});

/** Input for listing all tests in a workspace (admin/manager view) */
/**
 * #977 — a CES is "renewal due" when its retest/renewal date (`nextRetestAt`)
 * falls within this many days (overdue or upcoming). Items the nightly sweep
 * has already reopened have `nextRetestAt = null` (re-stamped on next approval)
 * and surface under their normal status instead. Shared by the server
 * `listAllTests` filter and the web tests-list "Renewal Due" predicate so the
 * two views stay in lockstep.
 */
export const RENEWAL_DUE_WINDOW_DAYS = 30;

/**
 * Epoch-ms cutoff for "renewal due": a CES is due when its `nextRetestAt` is at
 * or before this. Single source of the days→ms math so the server `listAllTests`
 * filter and the web tests-list predicate compute the identical boundary.
 */
export function renewalDueCutoffMs(now: number = Date.now()): number {
  return now + RENEWAL_DUE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

// Continuous-monitoring tests are the automated API checks (RFC-189): they
// re-run on a cadence and carry a `latestApiRunStatus` health verdict that is
// deliberately separate from the CES submission/review status.
export type MonitoringBucket =
  | "passing"
  | "failing"
  | "attention"
  | "awaiting"
  | "na";

export type MonitoringCounts = Record<MonitoringBucket, number>;

/**
 * Bucket a monitoring test by health — but a deactivated/N/A test never counts
 * as a problem: a check "error" on a control the company marked Not Applicable
 * is not something to act on, so it lands in its own `na` bucket regardless of
 * the last run's verdict.
 */
export function monitoringBucketOf(test: {
  status: ControlEvidenceStatusEnum;
  latestApiRunStatus?: ApiCheckHealth | null;
}): MonitoringBucket {
  if (test.status === "NOT_APPLICABLE") return "na";
  switch (test.latestApiRunStatus) {
    case "PASS":
      return "passing";
    case "FAIL":
      return "failing";
    case "ERROR":
    case "INCONCLUSIVE":
      return "attention";
    // RUNNING or null — no conclusive verdict yet.
    default:
      return "awaiting";
  }
}

export const ListAllTestsInputSchema = z.object({
  status: z.array(ControlEvidenceStatusEnumSchema).optional(),
  /** Filter current evidence preparation/monitoring instead of legacy execution state. */
  usePreparationStatus: z.boolean().optional(),
  /** Filter by the citation-aware user-facing status. */
  effectiveStatus: z.array(ControlEvidenceStatusEnumSchema).optional(),
  /** #977 — filter to tests whose renewal is due within RENEWAL_DUE_WINDOW_DAYS. */
  renewalDue: z.boolean().optional(),
  /** RFC-226 M3: only tests carrying auditor-request provenance
   *  (`executionConfig.auditRequestId`) — accepted proposals and any test an
   *  auditor request maps directly. */
  auditorRequested: z.boolean().optional(),
  frameworkId: z.string().optional(),
  /** RFC-215: requirement-derived visibility; default resolution is surface-specific. */
  requirementScope: z.enum(["ACTIVE", "OUT_OF_SCOPE", "ALL"]).optional(),
  search: z.string().optional(),
  sortBy: ListAllTestsSortBySchema.optional().default("manual"),
  sortOrder: SortOrderSchema.optional().default("asc"),
  limit: z.number().min(1).max(100).optional(),
  cursor: z.string().optional(),
  priority: z.array(PrioritySchema).optional(),
  overdueOnly: z.boolean().optional(),
  activeOnly: z.boolean().optional(),
  openRequestsOnly: z.boolean().optional(),
  labelIds: z.array(z.string()).optional(),
  templateType: TemplateTypeSchema.optional(),
  /** Provider-scoped monitoring; connection health remains account-specific. */
  provider: z.string().trim().min(1).optional(),
  assigneeId: z.string().optional(),
  assigneeFilter: z
    .enum(["assigned", "unassigned", "members", "agents"])
    .optional(),
  sourceType: z
    .enum(["screenata-canonical", "customer-visible"])
    .optional(),
  // Opt-in: populate TaskDTO.openRequestCount (one extra grouped query).
  // The Tasks board sets this; the Tests board doesn't need it.
  includeOpenRequestCounts: z.boolean().optional(),
  /** Badge-only API monitoring projection; returns counts without task details. */
  monitoringSummaryOnly: z.boolean().optional(),
  statCandidatesOnly: z.boolean().optional(),
});

/** Controls filter whole controls after their complete test population is rolled up. */
export const ControlsTreeFiltersSchema = ListAllTestsInputSchema.pick({
  search: true,
  assigneeId: true,
  assigneeFilter: true,
  labelIds: true,
  auditorRequested: true,
}).extend({
  status: z.enum(['all', 'done', 'failed', 'in_progress', 'todo', 'not_applicable', 'needs_work', 'in_review', 'approved']).optional(),
  hideNa: z.boolean().optional(),
  inclusion: z.enum(['core', 'scope', 'extended']).optional(),
  kind: z.enum(['policy', 'requirement', 'automated', 'manual']).optional(),
});
export type ControlsTreeFilters = z.infer<typeof ControlsTreeFiltersSchema>;
export interface ControlsTreeSummary {
  total: number;
  passed: number;
  failed: number;
  inProgress: number;
  notStarted: number;
  notApplicable: number;
}

export type ListAllTestsInput = z.infer<typeof ListAllTestsInputSchema>;

export type GetMyTasksInput = z.infer<typeof GetMyTasksInputSchema>;

// ===== Assign Test Input =====
export const AssignTestInputSchema = z.object({
  statusId: z.string(),
  assigneeId: z.string(),
  dueDate: z.date().optional(),
});

export type AssignTestInput = z.infer<typeof AssignTestInputSchema>;

// ===== Update Assignment Input =====
export const UpdateAssignmentInputSchema = z.object({
  statusId: z.string(),
  assigneeId: z.string().nullish(),
  dueDate: z.date().nullish(),
  externalTicketUrl: z.string().nullish(),
  frequency: EvidenceFrequencySchema.nullish(),
  priority: PrioritySchema.optional(),
});

export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInputSchema>;

export const BulkUpdateTestAssignmentsInputSchema = z.object({
  ids: z.array(z.string()).min(1).max(2000),
  assigneeId: z.string().nullish(),
  dueDate: z.date().nullish(),
  priority: PrioritySchema.optional(),
  // RFC-202: bulk role-typed Owner/Approver (ComplianceRole ids; null clears).
  ownerRoleId: z.string().nullish(),
  approverRoleId: z.string().nullish(),
});

export type BulkUpdateTestAssignmentsInput = z.infer<
  typeof BulkUpdateTestAssignmentsInputSchema
>;

// ===== Bulk Review Input =====
export const BulkReviewInputSchema = z.object({
  submissionIds: z.array(z.string()),
  decision: z.enum(["APPROVED", "NEEDS_REVISION"]),
  comments: z.string().optional(),
});

export type BulkReviewInput = z.infer<typeof BulkReviewInputSchema>;

// ===== Control Tree (RFC-098 Phase 3) =====
export const ControlTreeStatusSchema = z.enum([
  "PASSED",
  "FAILED",
  "NEEDS_REVIEW",
  "NOT_TESTED",
  "IN_PROGRESS",
  "PARTIAL",
  "NOT_APPLICABLE",
]);
export type ControlTreeStatus = z.infer<typeof ControlTreeStatusSchema>;

export const ControlTreeNodeSchema: z.ZodType<ControlTreeNode> = z.lazy(() =>
  z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(["framework", "category", "control", "anchor", "test"]),
    status: ControlTreeStatusSchema,
    approvalStage: z.enum(["PREPARE", "NEEDS_APPROVAL", "APPROVED", "CHANGES_REQUESTED", "NOT_APPLICABLE", "LEGACY_ACCEPTED"]).optional(),
    counts: z.object({
      passed: z.number(),
      failed: z.number(),
      needsReview: z.number(),
      notTested: z.number(),
      inProgress: z.number().optional(),
    }),
    children: z.array(ControlTreeNodeSchema).optional(),
    matchingTestCount: z.number().int().nonnegative().optional(),
    // RFC-193: for `anchor` nodes — the derived requirement state (mirrors
    // server AnchorState). The UI maps it to a label/tone via anchor-display.ts.
    anchorState: z
      .enum([
        "NOT_APPLICABLE",
        "NO_APPLICABLE_RESOURCES",
        "SATISFIED_WITH_EXCEPTION",
        "AUTOMATION_CONTRADICTS",
        "MANUALLY_SATISFIED",
        "VERIFIED_AUTOMATICALLY",
        "EVIDENCE_READY_FOR_REVIEW",
        "AUTOMATION_AVAILABLE",
        "NEEDS_MANUAL_EVIDENCE",
      ])
      .optional(),
    // RFC-193: on a `test` node, the anchor CES it rolls up to (null = not a covering check).
    coversAnchorId: z.string().nullable().optional(),
    checkRole: z.enum(["REQUIRED", "SUPPORTING"]).nullable().optional(),
    // For category nodes under criterion grouping: the trust-criterion text shown as a subtitle.
    description: z.string().optional(),
    // RFC-215: authoritative applicability for a published requirement node.
    // Absent on framework/control/Test and synthetic Unmapped/off-catalog rows.
    requirementScope: z
      .object({
        frameworkDefId: z.string(),
        frameworkControlId: z.string(),
        applicability: z.enum(['APPLICABLE', 'NOT_APPLICABLE']),
        source: z.enum(['MANUAL', 'AUTO_RULE', 'DEFAULT']),
        reason: z.string().nullable(),
        ruleKey: z.string().nullable(),
      })
      .optional(),
    // Control/test specific
    evidenceCount: z.number().optional(),
    lastTestedAt: z.date().nullable().optional(),
    retestCadenceDays: z.number().int().nullable().optional(),
    checkFrequencyDays: z.number().int().nullable().optional(),
    checkCron: z.string().nullable().optional(),
    checkInputs: z.record(z.unknown()).nullable().optional(),
    nextRetestAt: z.date().nullable().optional(),
    // RFC-234 inclusion on `test`/`anchor` leaves — derived at read time from
    // the published catalog, exactly like `TaskDTO.inclusion`, so the framework
    // requirement tree and the Tests list agree.
    inclusion: TestInclusionSchema.optional(),
    scopeFactLabel: z.string().nullable().optional(),
    collection: TestCollectionSchema.optional(),
    // RFC-226: the test carries auditor-request provenance (an AuditRequest
    // edge). Same "requested by auditor" filter the Tests list runs.
    auditorRequested: z.boolean().optional(),
    controlId: z.string().optional(), // For controls: CC6.1
    templateId: z.string().optional(), // For tests
    statusId: z.string().optional(), // For tests: ControlEvidenceStatus ID
    workflowVersion: z.number().optional(),
    preparation: TestEvidencePreparationSchema.optional(),
    evidenceStatus: ControlEvidenceStatusEnumSchema.optional(), // For tests: Raw database status (single source of truth)
    effectiveStatus: ControlEvidenceStatusEnumSchema.optional(), // RFC-215: raw status overlaid by all-citation scope
    applicableRequirementIds: z.array(z.string()).optional(), // #1704: in-scope requirements still demanding this Test
    // For API tests: latest automated check verdict, independent of evidenceStatus.
    latestApiRunStatus: ApiCheckHealthSchema.nullable().optional(),
    topicId: z.string().optional(), // For tests: Topic ID for navigation
    taskNumber: z.number().optional(), // For tests: Task number (0 for ad-hoc)
    taskCode: z.string().optional(), // For tests: Workspace-scoped display code (e.g., DEE-1)
    isAdHoc: z.boolean().optional(), // For tests: True if no template
    evidenceCategory: EvidenceCategorySchema.nullable().optional(),
    assignee: z
      .object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string(),
        image: z.string().nullable(),
      })
      .nullable()
      .optional(), // For tests: Assigned user info
    owner: z
      .object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().nullable(),
        image: z.string().nullable(),
      })
      .nullable()
      .optional(), // For controls: assigned owner (also acts as test approver)
    // RFC-107: Subtask stats for sampling tests
    subtaskStats: z
      .object({
        total: z.number(),
        completed: z.number(),
        inProgress: z.number(),
        notStarted: z.number(),
      })
      .nullable()
      .optional(),
    // RFC-107: Sampling workflow indicator (POLICY type)
    hasSample: z.boolean().nullable().optional(),
    // Labels for tests
    labels: z.array(LabelDTOSchema).optional(),
    // RFC-156: canonical multi-framework citations for control nodes.
    // Populated only on type='control' leaves. Each entry groups citations
    // by the framework they belong to; `citations[].isPrimary` flags the
    // primary citation for single-framework display fallback.
    frameworkCitations: z
      .array(
        z.object({
          frameworkSlug: z.string(),
          frameworkName: z.string(),
          citations: z.array(
            z.object({
              frameworkControlId: z.string(),
              citationTitle: z.string().nullable(),
              isPrimary: z.boolean(),
            }),
          ),
        }),
      )
      .optional(),
    // RFC-214 §13: slot-derived citations on `test`/`anchor` leaves — the
    // requirements THIS evidence ask satisfies. Deliberately the same shape
    // `TaskDTO.frameworkCitations` uses, so a tree row and a tests-list row
    // render one citation vocabulary. Distinct from `frameworkCitations`
    // above, which is the coarse CONTROL citation (RFC-214 §5 rule 1: the two
    // authorities are orthogonal, neither derived from the other).
    slotCitations: z.array(EvidenceSlotCitationDTOSchema).optional(),
  }),
);

export interface ControlTreeNode {
  workflowVersion?: number;
  preparation?: TestEvidencePreparation;
  /** Associated titles supplied for local text search. */
  searchTexts?: string[];
  id: string;
  name: string;
  type: "framework" | "category" | "control" | "anchor" | "test";
  status: ControlTreeStatus;
  /** Continuous Control approval, independent of Test coverage. */
  approvalStage?: "LEGACY_ACCEPTED" | "PREPARE" | "NEEDS_APPROVAL" | "APPROVED" | "CHANGES_REQUESTED" | "NOT_APPLICABLE";
  counts: {
    passed: number;
    failed: number;
    needsReview: number;
    notTested: number;
    inProgress?: number;
  };
  children?: ControlTreeNode[];
  /** Search-only match count; counts/status always describe the complete population. */
  matchingTestCount?: number;
  /** RFC-193: derived requirement state on `anchor` nodes (mirrors server AnchorState). */
  anchorState?:
    | "NOT_APPLICABLE"
    | "NO_APPLICABLE_RESOURCES"
    | "SATISFIED_WITH_EXCEPTION"
    | "AUTOMATION_CONTRADICTS"
    | "MANUALLY_SATISFIED"
    | "VERIFIED_AUTOMATICALLY"
    | "EVIDENCE_READY_FOR_REVIEW"
    | "AUTOMATION_AVAILABLE"
    | "NEEDS_MANUAL_EVIDENCE";
  /** RFC-193: on a `test` node, the anchor CES it rolls up to (null = not a covering check). */
  coversAnchorId?: string | null;
  checkRole?: "REQUIRED" | "SUPPORTING" | null;
  /** Category nodes under criterion grouping: trust-criterion text shown as a subtitle. */
  description?: string;
  /**
   * RFC-235: on a control row shown once under its primary category in the
   * pinned Controls lens — the other criteria that also cite it. Derived on
   * the client from the N:M criterion tree; the server never sets it.
   */
  alsoUnder?: string[];
  /** RFC-215: authoritative scope on published requirement category nodes. */
  requirementScope?: {
    frameworkDefId: string;
    frameworkControlId: string;
    applicability: 'APPLICABLE' | 'NOT_APPLICABLE';
    source: 'MANUAL' | 'AUTO_RULE' | 'DEFAULT';
    reason: string | null;
    ruleKey: string | null;
  };
  evidenceCount?: number;
  lastTestedAt?: Date | null;
  /** RFC-234 inclusion on test/anchor leaves — derived at read time. */
  inclusion?: TestInclusion;
  scopeFactLabel?: string | null;
  collection?: TestCollection;
  /** RFC-226: the test carries auditor-request provenance (an AuditRequest edge). */
  auditorRequested?: boolean;
  controlId?: string;
  /**
   * RFC-235: the customer-facing code of a catalog control, derived from its
   * domain and position (`AUTHZ-07`). Set only on the Controls lens tree;
   * `controlId` stays the stable key for links and search.
   */
  displayCode?: string;
  templateId?: string;
  statusId?: string;
  evidenceStatus?: ControlEvidenceStatusEnum; // For tests: Raw database status (single source of truth)
  /** RFC-215: effective Test status after all enrolled citations are resolved. */
  effectiveStatus?: ControlEvidenceStatusEnum;
  /** #1704: in-scope requirements still demanding this Test — why an active
   *  row under an N/A requirement is still there ("kept in scope by CC6.1"). */
  applicableRequirementIds?: string[];
  /** For API tests: latest automated check verdict, independent of evidenceStatus (RFC-189). */
  latestApiRunStatus?: ApiCheckHealth | null;
  topicId?: string; // For tests: Topic ID for navigation
  taskNumber?: number; // For tests: Task number (0 for ad-hoc)
  taskCode?: string; // For tests: Workspace-scoped display code (e.g., DEE-1)
  isAdHoc?: boolean; // For tests: True if no template
  templateType?: "POLICY" | "API" | "CONTAINER" | "REPORT"; // For template-backed tests
  evidenceCategory?: z.infer<typeof EvidenceCategorySchema> | null;
  assignee?: {
    id: string;
    name: string | null;
    email: string;
    image: string | null;
  } | null;
  owner?: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  } | null;
  // RFC-104: Sample-based evidence (one evidence covers entire sample)
  sample?: {
    id: string;
    sampleSize: number;
    passedCount: number | null;
    failedCount: number | null;
    pendingCount: number | null;
    populationName: string;
    populationType: string;
  } | null;
  // RFC-107: Subtask stats for sampling tests
  subtaskStats?: {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
  } | null;
  // RFC-107: Sampling workflow indicator (POLICY type)
  hasSample?: boolean | null;
  // Labels for tests
  labels?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  // Due date for tests
  dueDate?: Date | null;
  // #977: renewal/retest date on test nodes — drives the "Renewal Due" filter.
  nextRetestAt?: Date | null;
  // RFC-156: canonical multi-framework citations on control leaves
  frameworkCitations?: Array<{
    frameworkSlug: string;
    frameworkName: string;
    citations: Array<{
      frameworkControlId: string;
      citationTitle: string | null;
      isPrimary: boolean;
    }>;
  }>;
  /** RFC-214 §13: slot-derived citations on test/anchor leaves. */
  slotCitations?: EvidenceSlotCitationDTO[];
}

/** Complete Controls-lens rows; detail-only metadata stays in the detail reader. */
export interface ControlTreeRowNodeDTO extends Pick<ControlTreeNode,
  'id' | 'name' | 'type' | 'status' | 'controlId' | 'displayCode' | 'approvalStage' |
  'evidenceStatus' | 'effectiveStatus' | 'taskCode' | 'taskNumber' | 'templateId' |
  'templateType' | 'collection' | 'inclusion' |
  'scopeFactLabel' | 'preparation' | 'latestApiRunStatus' |
  'subtaskStats' | 'hasSample' | 'slotCitations' | 'applicableRequirementIds' |
  'searchTexts' | 'matchingTestCount'> {
  /** Zero buckets are implicit on the wire, restored before the UI uses them. */
  counts: Partial<ControlTreeNode['counts']>;
  assigneeId?: string;
  children?: ControlTreeRowNodeDTO[];
}

export interface ControlTreeRowsDTO {
  nodes: ControlTreeRowNodeDTO[];
  assignees: Record<string, NonNullable<ControlTreeNode['assignee']>>;
}

// ===== Get Controls Tree Input =====
export const GetControlsTreeInputSchema = z.object({
  frameworkId: z.string().optional(),
  status: z.array(ControlTreeStatusSchema).optional(),
});

export type GetControlsTreeInput = z.infer<typeof GetControlsTreeInputSchema>;

// ===== RFC-107: Subtask DTOs for Per-Item Sampling Evidence =====

/**
 * Create subtasks from sample - input DTO
 */
export const CreateSubtasksFromSampleInputSchema = z.object({
  /** Parent test ID (ControlEvidenceStatus) */
  parentId: z.string(),
  /** Sample ID to generate subtasks from */
  sampleId: z.string(),
  /** Optional: Create subtasks only for specific sample item IDs */
  sampleItemIds: z.array(z.string()).optional(),
});

export type CreateSubtasksFromSampleInput = z.infer<
  typeof CreateSubtasksFromSampleInputSchema
>;

/**
 * Subtask generation result
 */
export const SubtaskGenerationResultDTOSchema = z.object({
  /** Number of subtasks created */
  createdCount: z.number(),
  /** IDs of created subtasks */
  subtaskIds: z.array(z.string()),
  /** Any errors during creation */
  errors: z
    .array(
      z.object({
        sampleItemId: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
});

export type SubtaskGenerationResultDTO = z.infer<
  typeof SubtaskGenerationResultDTOSchema
>;

/**
 * Parent test with subtasks - full DTO for detail view
 */
export const TestWithSubtasksDTOSchema = ControlEvidenceStatusDTOSchema.extend({
  // Subtasks are returned as TaskDTO[] for direct use with StatusRow component
  subTasks: z.array(TaskDTOSchema).optional(),
  subTaskStats: z
    .object({
      total: z.number(),
      completed: z.number(),
      inProgress: z.number(),
      notStarted: z.number(),
      approved: z.number(),
      needsRevision: z.number(),
    })
    .optional(),
});

export type TestWithSubtasksDTO = z.infer<typeof TestWithSubtasksDTOSchema>;

/**
 * Status rollup result - computed parent status based on subtasks
 */
export const StatusRollupResultSchema = z.object({
  computedStatus: ControlEvidenceStatusEnumSchema,
  stats: z.object({
    total: z.number(),
    byStatus: z.record(z.number()),
  }),
  /** Whether parent status should be updated */
  shouldUpdate: z.boolean(),
});

export type StatusRollupResult = z.infer<typeof StatusRollupResultSchema>;

/**
 * Compute parent status from subtask statuses
 * This is a pure function for status rollup logic
 *
 * ControlEvidenceStatusEnum values:
 * - NOT_STARTED, IN_PROGRESS, SUBMITTED, NEEDS_RETEST, APPROVED, NOT_APPLICABLE, NON_PERFORMING
 */
export function computeParentStatusFromSubtasks(
  subtaskStatuses: Array<{ status: string }>,
): StatusRollupResult {
  const total = subtaskStatuses.length;

  if (total === 0) {
    return {
      computedStatus: "NOT_STARTED",
      stats: { total: 0, byStatus: {} },
      shouldUpdate: false,
    };
  }

  // Count by status
  const byStatus: Record<string, number> = {};
  for (const { status } of subtaskStatuses) {
    byStatus[status] = (byStatus[status] || 0) + 1;
  }

  // Rollup logic using actual ControlEvidenceStatusEnum values:
  // - All APPROVED → APPROVED
  // - Any NEEDS_RETEST → NEEDS_RETEST (needs attention)
  // - Any IN_PROGRESS, SUBMITTED, or PASSED → IN_PROGRESS
  // - Otherwise → NOT_STARTED

  let computedStatus: string;

  if (byStatus["APPROVED"] === total) {
    computedStatus = "APPROVED";
  } else if (byStatus["NEEDS_RETEST"]) {
    computedStatus = "NEEDS_RETEST";
  } else if (byStatus["IN_PROGRESS"] || byStatus["SUBMITTED"] || byStatus["PASSED"]) {
    computedStatus = "IN_PROGRESS";
  } else {
    computedStatus = "NOT_STARTED";
  }

  return {
    computedStatus: computedStatus as any,
    stats: { total, byStatus },
    shouldUpdate: true,
  };
}

// ===== RFC-091 Extension: Partner Template Sharing DTOs =====

/** Visibility levels for templates */
export const TemplateVisibilitySchema = z.enum([
  "PUBLIC",
  "ORGANIZATION",
  "PARTNER_CLIENTS",
  "WORKSPACE",
]);
export type TemplateVisibility = z.infer<typeof TemplateVisibilitySchema>;

/** Input for sharing a template with partner clients */
export const ShareTemplateWithClientsInputSchema = z.object({
  templateId: z.string(),
  /** Client org IDs to share with. Omit or empty = all clients */
  clientOrgIds: z.array(z.string()).optional(),
  /** Allow clients to clone this template */
  clientsCanClone: z.boolean().default(true),
  /** Allow clients to view the source doc content */
  clientsCanViewSource: z.boolean().default(false),
});
export type ShareTemplateWithClientsInput = z.infer<
  typeof ShareTemplateWithClientsInputSchema
>;

/** Input for updating template sharing settings */
export const UpdateTemplateSharingInputSchema = z.object({
  templateId: z.string(),
  clientOrgIds: z.array(z.string()).optional(),
  clientsCanClone: z.boolean().optional(),
  clientsCanViewSource: z.boolean().optional(),
});
export type UpdateTemplateSharingInput = z.infer<
  typeof UpdateTemplateSharingInputSchema
>;

/** Shared template info for partner dashboard */
export const PartnerSharedTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  title: z.string(),
  framework: z
    .object({
      id: z.string(),
      name: z.string(),
      shortName: z.string().optional(),
    })
    .nullable(),
  controls: z.array(
    z.object({
      id: z.string(),
      controlId: z.string(),
      title: z.string(),
    }),
  ),
  /** True if shared with all clients */
  sharedWithAll: z.boolean(),
  /** Specific clients this is shared with */
  sharedWithClients: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  clientsCanClone: z.boolean(),
  clientsCanViewSource: z.boolean(),
  cloneCount: z.number(),
  updatedAt: z.date(),
});
export type PartnerSharedTemplateDTO = z.infer<
  typeof PartnerSharedTemplateDTOSchema
>;

/** Partner-shared template info for client view */
export const ClientPartnerTemplateDTOSchema = z.object({
  id: z.string(),
  docId: z.string(),
  title: z.string(),
  /** Content (only if clientsCanViewSource is true) */
  content: z.unknown().nullable(),
  partnerOrg: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  framework: z
    .object({
      id: z.string(),
      name: z.string(),
      shortName: z.string().optional(),
    })
    .nullable(),
  controls: z.array(
    z.object({
      id: z.string(),
      controlId: z.string(),
      title: z.string(),
    }),
  ),
  canClone: z.boolean(),
  canViewSource: z.boolean(),
  templateType: TemplateTypeSchema,
  scoringCriteria: ScoringCriteriaSchema.nullable(),
  executionConfig: TemplateExecutionConfigSchema.nullable().optional(),
  samplingConfig: TemplateSamplingConfigSchema.nullable().optional(),
  updatedAt: z.date(),
});
export type ClientPartnerTemplateDTO = z.infer<
  typeof ClientPartnerTemplateDTOSchema
>;

/** Response for listPartnerSharedTemplates (client-side) */
export const ListPartnerSharedTemplatesResponseSchema = z.object({
  templates: z.array(ClientPartnerTemplateDTOSchema),
  total: z.number(),
  hasPartner: z.boolean(),
  partnerName: z.string().optional(),
});
export type ListPartnerSharedTemplatesResponse = z.infer<
  typeof ListPartnerSharedTemplatesResponseSchema
>;
